import { useState, useEffect, useRef } from "react";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
// Light, warm, professional — like a premium wellness brand
const T = {
  bg: "#F7F5F2",
  bgCard: "#FFFFFF",
  bgMuted: "#F0EDE8",
  border: "#E8E3DC",
  borderMid: "#D4CEC5",
  text: "#1A1714",
  textSub: "#6B6560",
  textMuted: "#A09A93",
  accent: "#C8522A",       // warm terracotta
  accentLight: "#F2E8E3",
  accentMid: "#E8A080",
  teal: "#2A8C7A",
  tealLight: "#E3F2EF",
  gold: "#C49A3C",
  goldLight: "#F7F0E0",
  rose: "#B84870",
  roseLight: "#F5E8EF",
  blue: "#2E6FA8",
  blueLight: "#E3EEF7",
  green: "#3A8A50",
  greenLight: "#E5F3E8",
  fd: "'Playfair Display', serif",
  fb: "'DM Sans', sans-serif",
};

// ─── DATA ─────────────────────────────────────────────────────────────────────

const PLANS = [
  {
    id: "free", name: "Starter", price: "$0", period: "forever",
    color: T.textSub,
    features: ["Basic habit tracking (5 metrics)", "7-day history", "Daily log", "Community tips"],
    locked: ["Wearable sync", "Cycle tracking", "AI coaching", "Guided meditations", "Unlimited history"],
  },
  {
    id: "pro", name: "Pro", price: "$9.99", period: "/month", badge: "Most Popular",
    color: T.accent,
    features: ["All 13 metrics", "Wearable sync", "Cycle tracking", "Guided meditations", "30-day history", "Daily AI insights", "In-app support chat"],
    locked: ["Team sharing", "Custom integrations", "Priority support"],
  },
  {
    id: "elite", name: "Elite", price: "$19.99", period: "/month", badge: "Best Value",
    color: T.teal,
    features: ["Everything in Pro", "Unlimited history", "Team sharing", "Custom integrations", "Priority support", "1-on-1 coaching call/month", "Early access to new features"],
    locked: [],
  },
];

const TUTORIAL_STEPS = [
  {
    icon: "Today",
    title: "Track Your Day",
    body: "Your home screen shows today's performance score. Tap any metric card to log your data — sleep, steps, heart rate, hydration, and more. Everything in one place.",
    tip: "Green means you're on track. Tap any number to update it.",
  },
  {
    icon: "Cycle",
    title: "Understand Your Cycle",
    body: "The Cycle tab gives you personalized guidance based on your current phase — what to eat, how to exercise, and how to feel your best every week of the month.",
    tip: "Your phase affects energy, mood, and recovery. Honor it.",
  },
  {
    icon: "Meditate",
    title: "Guided Meditations",
    body: "Browse curated meditations under 15 minutes, filtered by your mood or cycle phase. Each session opens directly on YouTube.",
    tip: "Even 5 minutes daily creates measurable changes in 8 weeks.",
  },
  {
    icon: "Devices",
    title: "Connect Your Wearable",
    body: "Sync your Apple Watch, WHOOP, Garmin, Oura Ring, or Fitbit. Your bio metrics — HRV, resting heart rate, SpO2 — will auto-populate every morning.",
    tip: "Go to Devices in the tab bar to connect in one tap.",
  },
  {
    icon: "Chat",
    title: "Ask GRIND Anything",
    body: "Have a question about your metrics, your cycle, or your training? Our AI assistant is available 24/7 in the Support tab. We're always here.",
    tip: "Can't find an answer? We'll connect you with a real human.",
  },
];

const CYCLE_PHASES = {
  menstrual: {
    name: "Menstrual", days: "Day 1–5", color: T.rose, light: T.roseLight,
    energy: "Low", mood: "Introspective",
    description: "Estrogen and progesterone are at their lowest. Your body is working hard — honor the need for rest and warmth. This is a time of renewal, not weakness.",
    physical: ["Cramping possible", "Fatigue is normal", "Lower back sensitivity", "Bloating common"],
    emotional: ["Introspective & reflective", "Desire for solitude", "Heightened intuition", "Lower social energy"],
    nutrition: ["Iron-rich foods — spinach, lentils, red meat", "Warm cooked meals over raw", "Dark chocolate for magnesium", "Ginger or chamomile tea for cramps", "Avoid excess caffeine and alcohol"],
    activities: ["Gentle yoga or yin yoga", "Slow walks in nature", "Warm baths and self-care", "Reading and journaling", "Extra sleep — no guilt"],
    avoid: ["High-intensity training", "Heavy lifting", "Overcommitting socially"],
    supplements: ["Iron", "Magnesium glycinate", "Omega-3s", "Vitamin D"],
    meditationMood: "calming",
  },
  follicular: {
    name: "Follicular", days: "Day 6–13", color: T.gold, light: T.goldLight,
    energy: "Rising", mood: "Optimistic",
    description: "Estrogen is rising, bringing clarity, motivation, and optimism. This is your season of new beginnings — ideal for starting projects and social connection.",
    physical: ["Energy increasing daily", "Higher pain tolerance", "Skin often clearer", "Cervical fluid increasing"],
    emotional: ["Optimistic and outgoing", "Creative energy rising", "More risk-tolerant", "Higher confidence"],
    nutrition: ["Fermented foods for estrogen metabolism", "Cruciferous vegetables — broccoli, kale", "Lean proteins", "Flaxseed and pumpkin seeds", "Lighter meals work well"],
    activities: ["Running and cardio", "Strength training", "Cycling", "Try something new", "Group sports and classes"],
    avoid: ["Over-scheduling — energy feels limitless but isn't"],
    supplements: ["B vitamins", "Zinc", "Vitamin C", "Probiotics"],
    meditationMood: "energizing",
  },
  ovulatory: {
    name: "Ovulatory", days: "Day 14–17", color: T.green, light: T.greenLight,
    energy: "Peak", mood: "Social & Magnetic",
    description: "Estrogen and testosterone peak together. You are at your most articulate, magnetic, and physically capable. Use this window for peak performance.",
    physical: ["Peak physical strength", "Heightened libido", "Possible mild cramping", "Elevated body temperature"],
    emotional: ["Highly social and charismatic", "Confident and assertive", "Strong communication", "Most outward-focused phase"],
    nutrition: ["Anti-inflammatory foods", "Light raw foods tolerated well", "Fiber-rich — quinoa, flaxseed", "Antioxidants — berries, leafy greens", "Extra hydration"],
    activities: ["Personal records — push intensity", "HIIT training", "Public speaking or presentations", "Important meetings and negotiations", "Race days"],
    avoid: ["Neglecting recovery despite high energy"],
    supplements: ["Vitex (chasteberry)", "Magnesium", "B6", "Antioxidant complex"],
    meditationMood: "focus",
  },
  luteal: {
    name: "Luteal", days: "Day 18–28", color: T.blue, light: T.blueLight,
    energy: "Declining", mood: "Detail-Oriented",
    description: "Progesterone rises then both hormones drop. Early luteal is great for detail work; late luteal may bring PMS for many. This is a time to be gentle with yourself.",
    physical: ["Breast tenderness possible", "Bloating increasing", "Carb and sugar cravings", "Sleep may be disrupted"],
    emotional: ["Early: organized and detail-focused", "Late: emotional sensitivity rises", "Lower frustration tolerance", "Strong nesting instinct"],
    nutrition: ["Complex carbs — sweet potato, oats", "Magnesium-rich — dark chocolate, nuts", "Calcium-rich foods", "Reduce salt, alcohol, refined sugar", "Chamomile tea for PMS"],
    activities: ["Yoga and Pilates", "Walking and light hiking", "Swimming", "Journaling and creative projects", "Self-care rituals"],
    avoid: ["High-intensity late in phase", "Excessive social obligations", "Heavy alcohol consumption"],
    supplements: ["Magnesium glycinate — key for PMS", "Evening primrose oil", "Vitamin B6", "Vitex (chasteberry)"],
    meditationMood: "soothing",
  },
};

const INSIGHTS = {
  hrv:{ label:"HRV", unit:"ms", color:T.rose, max:100, device:["Apple Watch","WHOOP","Garmin","Oura"], description:"Heart Rate Variability measures the variation between heartbeats. Higher HRV signals better recovery, resilience, and nervous system health.", ranges:[{max:30,label:"Low",tip:"HRV is low — your body needs recovery. Prioritize sleep, rest, and light movement today.",color:T.rose},{max:60,label:"Moderate",tip:"Moderate HRV. You're recovered enough for moderate training. Avoid all-out efforts.",color:T.gold},{max:100,label:"Optimal",tip:"Excellent HRV! Your nervous system is well-recovered — ideal day for intense training.",color:T.green}]},
  rhr:{ label:"Resting HR", unit:"bpm", color:T.accent, max:100, device:["Apple Watch","WHOOP","Garmin","Fitbit"], description:"Your resting heart rate at rest. Lower is generally better — it reflects cardiovascular fitness over time.", ranges:[{max:50,label:"Athletic",tip:"Elite-level resting HR — exceptional cardiovascular fitness.",color:T.green},{max:65,label:"Excellent",tip:"Strong resting heart rate. You're in great cardiovascular shape.",color:T.green},{max:80,label:"Normal",tip:"Normal range. Consistent cardio training will lower this over time.",color:T.gold},{max:100,label:"Elevated",tip:"Elevated — can indicate stress, poor sleep, or illness. Rest and hydrate.",color:T.rose}]},
  spo2:{ label:"Blood Oxygen", unit:"%", color:T.blue, max:100, device:["Apple Watch","WHOOP","Garmin"], description:"Blood oxygen saturation shows how well your red blood cells are carrying oxygen. 95–100% is healthy.", ranges:[{max:94,label:"Low",tip:"Below 95% warrants attention. Avoid intense exercise and consult a doctor if persistent.",color:T.rose},{max:97,label:"Normal",tip:"Normal oxygen saturation — you're getting enough oxygen at rest.",color:T.gold},{max:100,label:"Optimal",tip:"Excellent blood oxygen levels. Lungs and circulation are working well.",color:T.green}]},
  strain:{ label:"Strain Score", unit:"/21", color:T.accent, max:21, device:["WHOOP"], description:"WHOOP's strain score (0–21) measures cardiovascular load on your body. Balance high strain days with proper recovery.", ranges:[{max:7,label:"Light",tip:"Light day — great for recovery or active rest.",color:T.green},{max:14,label:"Moderate",tip:"Solid work. Good balance of effort.",color:T.gold},{max:18,label:"High",tip:"High strain — make sure your recovery matches your effort.",color:T.accent},{max:21,label:"All Out",tip:"Maximum effort. Prioritize sleep and nutrition tomorrow.",color:T.rose}]},
  recovery:{ label:"Recovery", unit:"%", color:T.green, max:100, device:["WHOOP","Garmin"], description:"Recovery is calculated from your HRV, resting heart rate, and sleep performance. It tells you how ready your body is to perform.", ranges:[{max:33,label:"Red",tip:"Body is signaling it needs rest — skip intense workouts today.",color:T.rose},{max:66,label:"Yellow",tip:"Moderate activity is fine. Listen to your body.",color:T.gold},{max:100,label:"Green",tip:"Primed to perform. Push hard today!",color:T.green}]},
  sleep:{ label:"Sleep", unit:"hrs", color:"#7060C8", max:10, device:["Apple Watch","WHOOP","Garmin","Oura"], description:"Sleep is your most powerful recovery tool. It's when muscle repairs, memory consolidates, and hormones rebalance.", ranges:[{max:5,label:"Insufficient",tip:"Under 5 hours impairs cognition, recovery, and immunity long-term.",color:T.rose},{max:7,label:"Below Optimal",tip:"Most adults need 7–9 hours. Even 30 more minutes makes a significant difference.",color:T.gold},{max:9,label:"Optimal",tip:"Perfect sleep window — body and brain are fully recovering.",color:T.green},{max:12,label:"Oversleeping",tip:"Consistently over 9 hours may indicate fatigue or illness.",color:T.gold}]},
  steps:{ label:"Steps", unit:"steps", color:T.teal, max:15000, device:["Apple Watch","Garmin","Fitbit"], description:"Daily step count is one of the strongest predictors of longevity — more impactful than almost any other single metric.", ranges:[{max:3000,label:"Sedentary",tip:"Very low movement today. Even short walks significantly reduce disease risk.",color:T.rose},{max:7500,label:"Lightly Active",tip:"Decent movement. Try to reach 8–10K for metabolic benefits.",color:T.gold},{max:12000,label:"Active",tip:"Hitting the longevity sweet spot. Keep it up!",color:T.green},{max:20000,label:"Very Active",tip:"Exceptional movement today.",color:T.green}]},
  calories:{ label:"Calories", unit:"kcal", color:T.gold, max:4000, device:["MyFitnessPal","Manual"], description:"Total daily caloric intake directly affects energy, body composition, and performance. Quality matters as much as quantity.", ranges:[{max:1200,label:"Under-fueled",tip:"Eating too little impairs recovery, hormones, and muscle retention.",color:T.rose},{max:2000,label:"Maintenance",tip:"Solid intake. Adjust based on your activity and goals.",color:T.green},{max:2800,label:"Performance",tip:"Good fueling for active days — focus on protein and complex carbs.",color:T.green},{max:4000,label:"High",tip:"Ensure these are quality sources, not just processed foods.",color:T.gold}]},
  water:{ label:"Hydration", unit:"oz", color:T.blue, max:128, device:["Manual","HidrateSpark"], description:"Hydration is essential for thermoregulation, joint lubrication, and nutrient transport. Most people are chronically dehydrated.", ranges:[{max:40,label:"Dehydrated",tip:"Mild dehydration impairs cognition and performance by up to 20%.",color:T.rose},{max:80,label:"Getting There",tip:"On the right track. Aim for half your bodyweight in ounces daily.",color:T.gold},{max:128,label:"Well Hydrated",tip:"Excellent hydration — your cells, joints, and kidneys are thriving.",color:T.green}]},
  mood:{ label:"Mood", unit:"/5", color:"#9060B8", max:5, device:["Manual"], description:"Emotional state impacts physical performance, decision-making, and recovery. Tracking mood reveals powerful patterns over time.", ranges:[{max:1,label:"Struggling",tip:"Tough day. Movement, sunlight, and human connection all help.",color:T.rose},{max:2,label:"Low",tip:"Below baseline. Check sleep, nutrition, and social connection.",color:T.gold},{max:3,label:"Neutral",tip:"Normal baseline — small wins can shift this.",color:T.gold},{max:4,label:"Good",tip:"Positive baseline. Keep building momentum.",color:T.green},{max:5,label:"Excellent",tip:"Thriving — high mood correlates with better decisions and creativity.",color:T.green}]},
  weight:{ label:"Weight", unit:"lbs", color:T.textSub, max:300, device:["Withings","RENPHO","Manual"], description:"Track trends over weeks, not days. Daily fluctuations of 2–5 lbs are completely normal due to water, food, and hormones.", ranges:[]},
  reading:{ label:"Reading", unit:"pages", color:T.teal, max:60, device:["Kindle","Manual"], description:"Reading improves focus, empathy, vocabulary, and mental health. Even 10 pages a day compounds to roughly 12 books per year.", ranges:[{max:10,label:"Light",tip:"10 pages daily compounds to 3,650 pages per year — about 12 books.",color:T.gold},{max:30,label:"Solid",tip:"Consistent readers report lower stress and higher empathy.",color:T.green},{max:60,label:"Avid",tip:"Research links reading to slower cognitive decline and greater life satisfaction.",color:T.green}]},
  mindfulness:{ label:"Mindfulness", unit:"mins", color:T.rose, max:30, device:["Apple Watch","Headspace","Calm"], description:"Mindfulness reduces cortisol, improves focus, and enhances emotional regulation. Consistent practice literally rewires the brain.", ranges:[{max:5,label:"Starting",tip:"5 minutes daily shows measurable brain changes in just 8 weeks.",color:T.gold},{max:15,label:"Building",tip:"Building the prefrontal cortex and reducing amygdala reactivity.",color:T.green},{max:30,label:"Deep Practice",tip:"This level of practice produces significant changes in stress hormones.",color:T.green}]},
};

const MEDITATIONS = {
  calming: [
    { title: "5-Min Body Scan for Rest & Recovery", channel: "Yoga With Adriene", duration: "5:42", videoId: "tbfXEeYHxMk", tags: ["rest", "body scan", "gentle"] },
    { title: "10-Min Restorative Breathing", channel: "Great Meditation", duration: "10:00", videoId: "inpok4MKVLM", tags: ["breathing", "relaxation", "hormones"] },
    { title: "Guided Sleep Meditation", channel: "Michael Sealey", duration: "14:52", videoId: "1vx8iUvfyCY", tags: ["sleep", "evening", "deep rest"] },
    { title: "5-Min Stress Relief", channel: "Headspace", duration: "5:00", videoId: "ux_TTHk8yBs", tags: ["stress", "quick", "breathing"] },
  ],
  energizing: [
    { title: "Morning Clarity Meditation", channel: "Goodful", duration: "10:00", videoId: "inpok4MKVLM", tags: ["morning", "energy", "motivation"] },
    { title: "Confidence & Empowerment", channel: "Insight Timer", duration: "12:00", videoId: "86m4RC_ADEY", tags: ["confidence", "power", "strength"] },
    { title: "7-Min Energizing Breathwork", channel: "Yoga With Adriene", duration: "7:14", videoId: "tbfXEeYHxMk", tags: ["breath", "energy", "vitality"] },
    { title: "Uplifting Morning Visualization", channel: "Jason Stephenson", duration: "13:00", videoId: "O-6f5wQXSu8", tags: ["morning", "visualization", "positive"] },
  ],
  focus: [
    { title: "10-Min Deep Focus", channel: "Huberman Lab", duration: "10:00", videoId: "tybOi4hjZFQ", tags: ["focus", "performance", "clarity"] },
    { title: "Peak Performance Mindset", channel: "Mindfulness Coach", duration: "12:30", videoId: "86m4RC_ADEY", tags: ["performance", "mindset", "success"] },
    { title: "Clarity & Intention Setting", channel: "Calm", duration: "5:00", videoId: "ux_TTHk8yBs", tags: ["clarity", "intention", "morning"] },
    { title: "Flow State Concentration", channel: "Binaural Beats", duration: "14:00", videoId: "1vx8iUvfyCY", tags: ["flow", "deep work", "concentration"] },
  ],
  soothing: [
    { title: "PMS Relief Meditation", channel: "The Honest Guys", duration: "11:00", videoId: "O-6f5wQXSu8", tags: ["PMS", "relief", "hormone support"] },
    { title: "Emotional Balance for Women", channel: "Sarah Blondin", duration: "13:30", videoId: "tybOi4hjZFQ", tags: ["emotions", "balance", "women"] },
    { title: "Luteal Phase Wind-Down", channel: "Yoga Nidra Network", duration: "14:52", videoId: "inpok4MKVLM", tags: ["luteal", "wind down", "evening"] },
    { title: "Self-Compassion Practice", channel: "Tara Brach", duration: "12:00", videoId: "86m4RC_ADEY", tags: ["self-love", "compassion", "gentle"] },
  ],
  stressed: [
    { title: "5-Min Anxiety Relief", channel: "Therapy in a Nutshell", duration: "5:00", videoId: "ux_TTHk8yBs", tags: ["anxiety", "quick", "relief"] },
    { title: "Box Breathing Technique", channel: "Mark Divine", duration: "6:30", videoId: "tybOi4hjZFQ", tags: ["breathing", "stress", "proven technique"] },
    { title: "Cortisol Reset", channel: "Dr. Joe Dispenza", duration: "14:00", videoId: "1vx8iUvfyCY", tags: ["cortisol", "stress hormones", "reset"] },
    { title: "Progressive Muscle Relaxation", channel: "Anxiety Relief", duration: "12:00", videoId: "inpok4MKVLM", tags: ["muscle tension", "relaxation", "body"] },
  ],
};
const MOOD_MED = { 1:"stressed",2:"soothing",3:"calming",4:"calming",5:"energizing" };

const WEARABLES = [
  { id:"apple", name:"Apple Watch", metrics:["hrv","rhr","spo2","sleep","steps","mindfulness"] },
  { id:"whoop", name:"WHOOP", metrics:["hrv","rhr","strain","recovery","sleep"] },
  { id:"garmin", name:"Garmin", metrics:["hrv","rhr","sleep","steps","spo2"] },
  { id:"oura", name:"Oura Ring", metrics:["hrv","rhr","sleep","spo2"] },
  { id:"fitbit", name:"Fitbit", metrics:["rhr","sleep","steps","spo2"] },
  { id:"flo", name:"Flo App", metrics:["cycle","mood","symptoms"] },
];

const SCREEN_TIME = [
  { name:"Social Media", mins:87, color:T.rose },
  { name:"Productivity", mins:142, color:T.green },
  { name:"Entertainment", mins:63, color:T.gold },
  { name:"Health & Fitness", mins:24, color:T.teal },
  { name:"News", mins:31, color:T.blue },
];

const ACTIVITIES_LIST = [
  "Yoga","Cycling","Swimming","Boxing","Strength Training","Walking","Running","Tennis","Basketball","Climbing"
];

const SYMPTOMS_LIST = ["Cramps","Bloating","Headache","Back pain","Breast tenderness","Fatigue","Nausea","Mood swings","Cravings","Spotting","Acne","Insomnia"];

// AI Chat knowledge base
const AI_KB = [
  { q:["what is hrv","hrv mean","heart rate variability"], a:"HRV (Heart Rate Variability) measures the variation in time between your heartbeats. Higher HRV generally means better recovery and resilience. Athletes typically have HRV between 55–100ms. If yours is low, prioritize sleep and rest that day." },
  { q:["resting heart rate","rhr"], a:"Resting heart rate is how many times your heart beats per minute at rest. Lower is usually better — 50–65 bpm is excellent, 65–80 is normal. It tends to decrease over time with consistent cardiovascular training." },
  { q:["blood oxygen","spo2","oxygen saturation"], a:"Blood oxygen (SpO2) measures how much oxygen your red blood cells are carrying. 95–100% is healthy. If it drops below 94%, avoid intense exercise and consider speaking with a doctor if it persists." },
  { q:["how much sleep","sleep","how many hours"], a:"Most adults need 7–9 hours of sleep per night. Sleep is when your body repairs muscle, consolidates memory, and balances hormones. It's your single most powerful recovery tool — more impactful than any supplement." },
  { q:["water","hydration","how much water"], a:"A good target is half your body weight in ounces daily. For example, a 150 lb person would aim for 75 oz. Dehydration — even mild — can impair cognition and physical performance by up to 20%." },
  { q:["steps","how many steps","walking"], a:"Research shows 7,500–10,000 steps per day is associated with significantly lower mortality risk. You don't need intense exercise — consistent daily walking is one of the most effective longevity habits." },
  { q:["cycle","period","menstrual","ovulation","phases"], a:"The menstrual cycle has four phases: Menstrual (days 1–5), Follicular (days 6–13), Ovulatory (days 14–17), and Luteal (days 18–28). Each phase affects your energy, mood, and what kinds of exercise or nutrition work best. Tap the Cycle tab to explore your current phase." },
  { q:["meditation","meditate","mindfulness"], a:"Even 5–10 minutes of daily meditation creates measurable changes in brain structure within 8 weeks. It lowers cortisol, improves focus, and enhances emotional regulation. Head to the Meditate tab to find a session matched to your mood or cycle phase." },
  { q:["whoop","apple watch","garmin","oura","fitbit","wearable","device"], a:"GRIND syncs with Apple Watch, WHOOP, Garmin, Oura Ring, and Fitbit. In a production app, this connects via Apple HealthKit, WHOOP API, and Garmin Connect. Go to the Devices tab to toggle your device connections." },
  { q:["calories","how many calories","nutrition","diet"], a:"Caloric needs vary by person, but most active women need 1,800–2,400 kcal daily and men 2,200–3,000 kcal. Quality matters as much as quantity — prioritize whole foods, lean proteins, and complex carbohydrates." },
  { q:["cancel","subscription","billing","refund","upgrade","downgrade"], a:"For billing and subscription questions, please contact our support team directly at cbowman706@gmail.com — they'll take care of you within 24 hours." },
  { q:["support","help","problem","issue","bug","contact"], a:"For technical support or urgent questions, reach our team at cbowman706@gmail.com. Include a description of your issue and we'll get back to you within 24 hours." },
  { q:["pms","luteal","progesterone","menstrual cramps"], a:"During your luteal phase, rising then falling progesterone can trigger PMS symptoms like bloating, mood swings, and fatigue. Magnesium glycinate (300–400mg), Vitamin B6, and reducing caffeine and alcohol are evidence-based ways to ease symptoms." },
  { q:["mood","emotional","anxiety","stress","feeling"], a:"Your mood is directly connected to sleep quality, hydration, blood sugar stability, and movement. If you're consistently below a 3, consider adding a 10-minute walk, improving your sleep schedule, and checking hydration. The Reflect tab also has guided journaling to help process emotions." },
  { q:["recovery","whoop recovery","how to recover"], a:"Recovery is best supported by 7–9 hours of sleep, adequate protein intake (0.7–1g per lb of bodyweight), hydration, and managing stress. Consistent green recovery days are built over weeks of consistent healthy habits — not one perfect night." },
];

const today = () => new Date().toISOString().split("T")[0];
const getDayLabel = (offset) => { const d=new Date(); d.setDate(d.getDate()-offset); return d.toISOString().split("T")[0]; };
const WEEK = Array.from({length:7},(_,i)=>getDayLabel(6-i));
const DAY_NAMES = ["SUN","MON","TUE","WED","THU","FRI","SAT"];

function getRange(key, val) {
  const m = INSIGHTS[key]; if(!m||!m.ranges||!m.ranges.length) return null;
  return m.ranges.find(r=>val<=r.max)||m.ranges[m.ranges.length-1];
}

function askAI(question) {
  const q = question.toLowerCase();
  for(const entry of AI_KB) {
    if(entry.q.some(kw => q.includes(kw))) return entry.a;
  }
  return `That's a great question. I don't have a specific answer for that yet, but our support team would love to help! Reach out at **cbowman706@gmail.com** and we'll get back to you within 24 hours.`;
}

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

function Badge({ children, color = T.accent, bg }) {
  return (
    <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:20, background:bg||color+"18", fontFamily:T.fb, fontSize:11, fontWeight:600, color, letterSpacing:0.5 }}>
      {children}
    </span>
  );
}

function Card({ children, style, onClick }) {
  return (
    <div onClick={onClick} style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:20, padding:20, ...style, cursor:onClick?"pointer":"default" }}>
      {children}
    </div>
  );
}

function PillBtn({ children, active, color=T.accent, onClick, style }) {
  return (
    <button onClick={onClick} style={{ padding:"8px 18px", borderRadius:24, border:`1.5px solid ${active?color:T.border}`, background:active?color+"14":T.bgCard, fontFamily:T.fb, fontSize:13, fontWeight:500, color:active?color:T.textSub, cursor:"pointer", transition:"all .15s", whiteSpace:"nowrap", ...style }}>
      {children}
    </button>
  );
}

function Radial({ value, max, color, size=80, stroke=7, children }) {
  const r=(size-stroke*2)/2, circ=2*Math.PI*r, pct=Math.min(value/max,1);
  return (
    <div style={{position:"relative",width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.bgMuted} strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={circ*(1-pct)} strokeLinecap="round"
          style={{transition:"stroke-dashoffset 0.8s ease"}}/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column"}}>{children}</div>
    </div>
  );
}

function Spark({ data, color, h=32, w=80 }) {
  if(!data||data.length<2) return null;
  const mn=Math.min(...data),mx=Math.max(...data),rng=mx-mn||1;
  const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-((v-mn)/rng)*(h-4)-2}`).join(" ");
  const last=pts.split(" ").pop().split(",");
  return (
    <svg width={w} height={h} style={{overflow:"visible"}}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={parseFloat(last[0])} cy={parseFloat(last[1])} r={3} fill={color}/>
    </svg>
  );
}

function Modal({ onClose, children, wide }) {
  return (
    <div style={{ position:"fixed",inset:0,zIndex:300,background:"rgba(26,23,20,.5)",display:"flex",alignItems:"flex-end",justifyContent:"center",backdropFilter:"blur(6px)" }}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.bgCard,borderRadius:"28px 28px 0 0",padding:"0 24px 48px",width:"100%",maxWidth:430,maxHeight:"92vh",overflowY:"auto",animation:"slideUp .3s ease" }}>
        <div style={{ width:36,height:4,background:T.border,borderRadius:2,margin:"16px auto 24px",display:"block" }}/>
        {children}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

export default function App() {
  // Auth flow state
  // ─── SCREEN + AUTH ───────────────────────────────────────────────────────────
  // If returning from WHOOP OAuth, skip splash entirely
  const _hasOAuthCode = new URLSearchParams(window.location.search).get("code") && 
    new URLSearchParams(window.location.search).get("state")==="grind_whoop";
  const [screen, setScreen] = useState(_hasOAuthCode ? "app" : "splash");
  const [authForm, setAuthForm] = useState({ name:"", email:"", password:"", confirmPassword:"" });
  const [authErr, setAuthErr] = useState("");
  const [authMode, setAuthMode] = useState("signup");
  const [tutorialStep, setTutorialStep] = useState(0);

  // Persist user, profile, log data in localStorage
  const [user, setUser] = useState(()=>{ try{const u=localStorage.getItem("grind_user");return u?JSON.parse(u):null;}catch{return null;} });
  const [isFemale, setIsFemale] = useState(()=>{ try{return localStorage.getItem("grind_female")!=="false";}catch{return true;} });
  const [profile, setProfile] = useState(()=>{ try{const p=localStorage.getItem("grind_profile");return p?JSON.parse(p):{name:"",username:"",email:"",age:"",city:"",state:"",favActivity:"",bio:""};}catch{return {name:"",username:"",email:"",age:"",city:"",state:"",favActivity:"",bio:""};} });
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState(null);

  // Splash → welcome/app after 2.2s
  useEffect(()=>{
    if(screen==="splash"){
      const t=setTimeout(()=>setScreen(user?"app":"welcome"),2200);
      return ()=>clearTimeout(t);
    }
  },[screen,user]);

  // Persist user changes
  useEffect(()=>{ if(user) localStorage.setItem("grind_user",JSON.stringify(user)); },[user]);
  useEffect(()=>{ localStorage.setItem("grind_female",String(isFemale)); },[isFemale]);
  useEffect(()=>{ localStorage.setItem("grind_profile",JSON.stringify(profile)); },[profile]);

  // ─── APP STATE ────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState("today");
  const [selectedDate, setSelectedDate] = useState(today());
  const [connectedDevices, setConnectedDevices] = useState(()=>{ try{const d=localStorage.getItem("grind_devices");return d?JSON.parse(d):[];}catch{return [];} });
  const [activeMetric, setActiveMetric] = useState(null);
  const [editMetric, setEditMetric] = useState(null);
  const [inputVal, setInputVal] = useState("");
  const [celebrateKey, setCelebrateKey] = useState(null);
  const [meditationFilter, setMeditationFilter] = useState("all");
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [cycleTab, setCycleTab] = useState("overview");
  const [cycleData, setCycleData] = useState(()=>{ try{const c=localStorage.getItem("grind_cycle");return c?JSON.parse(c):{lastPeriodStart:getDayLabel(14),cycleLength:28,periodLength:5};}catch{return {lastPeriodStart:getDayLabel(14),cycleLength:28,periodLength:5};} });
  const [todaySymptoms, setTodaySymptoms] = useState([]);
  const [flowLevel, setFlowLevel] = useState(null);
  const [reflection, setReflection] = useState({ gratitude:"",wins:"",tomorrow:"",energy:3,stress:2,activities:ACTIVITIES_LIST.map(l=>({label:l,logged:false})) });
  const [reminders, setReminders] = useState(()=>{ try{const r=localStorage.getItem("grind_reminders");return r?JSON.parse(r):[
    {id:"r1",label:"Morning Workout",time:"07:00",days:[1,2,3,4,5],color:T.accent,enabled:true},
    {id:"r2",label:"Drink Water",time:"09:00",days:[0,1,2,3,4,5,6],color:T.blue,enabled:true},
    {id:"r3",label:"Evening Read",time:"21:00",days:[0,1,2,3,4,5,6],color:T.teal,enabled:true},
  ];}catch{return [];} });
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({label:"",time:"08:00",days:[1,2,3,4,5],color:T.accent});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ─── WHOOP OAUTH STATE ────────────────────────────────────────────────────────
  const [whoopToken, setWhoopToken] = useState(()=>{ try{return JSON.parse(localStorage.getItem("grind_whoop_token")||"null");}catch{return null;} });
  const [whoopProfile, setWhoopProfile] = useState(()=>{ try{return JSON.parse(localStorage.getItem("grind_whoop_profile")||"null");}catch{return null;} });
  const [whoopData, setWhoopData] = useState(()=>{ try{return JSON.parse(localStorage.getItem("grind_whoop_data")||"null");}catch{return null;} });
  const [whoopLoading, setWhoopLoading] = useState(false);
  const [whoopError, setWhoopError] = useState(null);
  const [whoopLastSync, setWhoopLastSync] = useState(()=>localStorage.getItem("grind_whoop_lastsync")||null);
  const [showWhoopSetup, setShowWhoopSetup] = useState(false);
  const [whoopClientId, setWhoopClientId] = useState(()=>localStorage.getItem("grind_whoop_client_id")||"");
  const [whoopClientSecret, setWhoopClientSecret] = useState(()=>localStorage.getItem("grind_whoop_client_secret")||"");

  const WHOOP_REDIRECT = window.location.origin + window.location.pathname;
  const WHOOP_SCOPES = "read:recovery read:cycles read:sleep read:workout read:body_measurement read:profile offline";

  const whoopAuthUrl = (clientId) =>
    `https://api.prod.whoop.com/oauth/oauth2/auth?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(WHOOP_REDIRECT)}&scope=${encodeURIComponent(WHOOP_SCOPES)}&state=grind_whoop`;

  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if(code && state==="grind_whoop"){
      window.history.replaceState({}, document.title, window.location.pathname);
      exchangeWhoopCode(code);
    }
  // eslint-disable-next-line
  },[]);

  const exchangeWhoopCode = async (code) => {
    setWhoopLoading(true); setWhoopError(null);
    const clientId = localStorage.getItem("grind_whoop_client_id")||"";
    const clientSecret = localStorage.getItem("grind_whoop_client_secret")||"";
    if(!clientId||!clientSecret){ setWhoopError("Missing WHOOP credentials."); setWhoopLoading(false); return; }
    try {
      // Token exchange via Netlify function (avoids CORS - browser can't call WHOOP token endpoint directly)
      const res = await fetch("/.netlify/functions/whoop-token", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ grant_type:"authorization_code", code, client_id:clientId, client_secret:clientSecret, redirect_uri:WHOOP_REDIRECT })
      });
      if(!res.ok) throw new Error(`Token exchange failed (${res.status})`);
      const token = await res.json();
      token.expires_at = Date.now() + (token.expires_in||3600)*1000;
      setWhoopToken(token); localStorage.setItem("grind_whoop_token", JSON.stringify(token));
      await fetchWhoopData(token);
      setConnectedDevices(prev=>prev.includes("whoop")?prev:[...prev,"whoop"]);
    } catch(e){ setWhoopError(e.message); }
    setWhoopLoading(false);
  };

  const refreshWhoopToken = async (token) => {
    const clientId = localStorage.getItem("grind_whoop_client_id")||"";
    const clientSecret = localStorage.getItem("grind_whoop_client_secret")||"";
    const res = await fetch("/.netlify/functions/whoop-token", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ grant_type:"refresh_token", refresh_token:token.refresh_token, client_id:clientId, client_secret:clientSecret })
    });
    if(!res.ok) throw new Error("Token refresh failed");
    const newToken = await res.json();
    newToken.expires_at = Date.now() + (newToken.expires_in||3600)*1000;
    localStorage.setItem("grind_whoop_token", JSON.stringify(newToken));
    setWhoopToken(newToken); return newToken;
  };

  const whoopFetch = async (url, token) => {
    let t = token;
    if(t.expires_at && Date.now() > t.expires_at - 60000) t = await refreshWhoopToken(t);
    const res = await fetch(url, { headers:{ Authorization:`Bearer ${t.access_token}` } });
    if(!res.ok) throw new Error(`WHOOP API ${res.status}`);
    return res.json();
  };

  const fetchWhoopData = async (tokenArg) => {
    setWhoopLoading(true); setWhoopError(null);
    try {
      const t = tokenArg || whoopToken; if(!t) return;
      const [pd, rd, sd, cd] = await Promise.allSettled([
        whoopFetch("https://api.prod.whoop.com/developer/v1/user/profile/basic", t),
        whoopFetch("https://api.prod.whoop.com/developer/v1/recovery?limit=1", t),
        whoopFetch("https://api.prod.whoop.com/developer/v1/activity/sleep?limit=1", t),
        whoopFetch("https://api.prod.whoop.com/developer/v1/cycle?limit=1", t),
      ]);
      const prof = pd.status==="fulfilled" ? pd.value : null;
      const rec = rd.status==="fulfilled" ? rd.value?.records?.[0] : null;
      const slp = sd.status==="fulfilled" ? sd.value?.records?.[0] : null;
      const cyc = cd.status==="fulfilled" ? cd.value?.records?.[0] : null;
      if(prof){ setWhoopProfile(prof); localStorage.setItem("grind_whoop_profile",JSON.stringify(prof)); }
      const metrics = {};
      if(rec?.score){ metrics.recovery=Math.round(rec.score.recovery_score); metrics.hrv=Math.round(rec.score.hrv_rmssd_milli); metrics.rhr=Math.round(rec.score.resting_heart_rate); }
      if(slp?.score){ metrics.sleep=parseFloat(((slp.score.stage_summary?.total_in_bed_time_milli||0)/3600000).toFixed(1)); }
      if(cyc?.score){ metrics.strain=parseFloat((cyc.score.strain||0).toFixed(1)); if(cyc.score.kilojoule) metrics.calories=Math.round(cyc.score.kilojoule/4.184); }
      const result = { profile:prof, recovery:rec, sleep:slp, cycle:cyc, metrics, fetchedAt:Date.now() };
      setWhoopData(result); localStorage.setItem("grind_whoop_data",JSON.stringify(result));
      if(Object.keys(metrics).length>0){
        const tk=today(); setLogData(prev=>({...prev,[tk]:{...(prev[tk]||{}),...metrics}})); setLiveLog(prev=>({...prev,...metrics}));
      }
      const now=new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
      setWhoopLastSync(now); localStorage.setItem("grind_whoop_lastsync",now);
    } catch(e){ setWhoopError(e.message); }
    setWhoopLoading(false);
  };

  const disconnectWhoop = () => {
    setWhoopToken(null); setWhoopProfile(null); setWhoopData(null); setWhoopLastSync(null); setWhoopError(null);
    ["grind_whoop_token","grind_whoop_profile","grind_whoop_data","grind_whoop_lastsync"].forEach(k=>localStorage.removeItem(k));
    setConnectedDevices(prev=>prev.filter(d=>d!=="whoop"));
  };

  // Persist app state
  useEffect(()=>{ localStorage.setItem("grind_devices",JSON.stringify(connectedDevices)); },[connectedDevices]);
  useEffect(()=>{ localStorage.setItem("grind_cycle",JSON.stringify(cycleData)); },[cycleData]);
  useEffect(()=>{ localStorage.setItem("grind_reminders",JSON.stringify(reminders)); },[reminders]);

  // ─── ACTIVITY LOG ─────────────────────────────────────────────────────────────
  const SPORTS = [
    "Running","Walking","Cycling","Swimming","Basketball","Soccer","Tennis","Golf","Volleyball","Baseball",
    "Football","Boxing","Yoga","Pilates","Hiking","Rock Climbing","CrossFit","Weightlifting","HIIT","Stretching",
    "Dancing","Rowing","Skiing","Snowboarding","Martial Arts","Pickleball","Other"
  ];
  const [activityLog, setActivityLog] = useState(()=>{ try{const a=localStorage.getItem("grind_activities");return a?JSON.parse(a):[];}catch{return [];} });
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({sport:"Running",duration:30,intensity:"Moderate",notes:"",date:today()});
  useEffect(()=>{ localStorage.setItem("grind_activities",JSON.stringify(activityLog)); },[activityLog]);

  // ─── REAL METRIC LOG (zero-based, localStorage) ───────────────────────────────
  const [logData, setLogData] = useState(()=>{
    try{ const l=localStorage.getItem("grind_log"); return l?JSON.parse(l):{}; }catch{ return {}; }
  });
  useEffect(()=>{ localStorage.setItem("grind_log",JSON.stringify(logData)); },[logData]);
  const [liveLog, setLiveLog] = useState({});
  const todayData = { ...(logData[selectedDate]||{}), ...liveLog };

  const saveMetric = (key, val) => {
    const num=parseFloat(val); if(isNaN(num)) return;
    const updated = { ...(logData[selectedDate]||{}), [key]:num };
    setLogData(prev=>({...prev,[selectedDate]:updated}));
    setLiveLog(prev=>({...prev,[key]:num}));
    setCelebrateKey(key); setTimeout(()=>setCelebrateKey(null),600);
    setEditMetric(null); setInputVal("");
  };

  // ─── CHAT ─────────────────────────────────────────────────────────────────────
  const [chatMessages, setChatMessages] = useState([
    { role:"assistant", text:`Hi${profile?.name?" "+profile.name:user?.name?" "+user.name:""}! I'm your GRIND wellness assistant. Ask me anything about your metrics, your cycle, exercise, nutrition, or how to use the app.` }
  ]);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef(null);

  const primaryMetrics = ["hrv","rhr","spo2","recovery","sleep","steps","calories","water","mood","weight","reading","mindfulness"];
  const connectedMetrics = connectedDevices.flatMap(id=>WEARABLES.find(w=>w.id===id)?.metrics||[]);
  const totalLogged = primaryMetrics.filter(k=>todayData[k]!==undefined&&todayData[k]>0).length;
  const weekHistory = (key) => WEEK.map(d=>(logData[d]||{})[key]||0);

  // Streak: consecutive days with at least 3 metrics logged
  const streak = (()=>{ let s=0; for(let i=0;i<30;i++){ const d=getDayLabel(i); if(Object.keys(logData[d]||{}).length>=3)s++; else break; } return s; })();

  // Behavior suggestions based on logged history
  const getBehaviorInsight = () => {
    const avgSleep = weekHistory("sleep").filter(v=>v>0);
    const avgMood = weekHistory("mood").filter(v=>v>0);
    const avgSteps = weekHistory("steps").filter(v=>v>0);
    const sleepAvg = avgSleep.length ? avgSleep.reduce((a,b)=>a+b,0)/avgSleep.length : 0;
    const moodAvg = avgMood.length ? avgMood.reduce((a,b)=>a+b,0)/avgMood.length : 0;
    const stepsAvg = avgSteps.length ? avgSteps.reduce((a,b)=>a+b,0)/avgSteps.length : 0;
    if(sleepAvg>0 && sleepAvg<6.5) return `Based on your history, you're averaging ${sleepAvg.toFixed(1)} hours of sleep — below optimal. Even 30 extra minutes tonight could meaningfully improve your mood and recovery tomorrow.`;
    if(moodAvg>0 && moodAvg<3) return `Your mood has been trending low this week. Consider a short walk, more hydration, or a 5-minute meditation. Small inputs create real shifts.`;
    if(stepsAvg>0 && stepsAvg>9000) return `You've been consistently hitting strong step counts — averaging ${Math.round(stepsAvg).toLocaleString()} steps this week. That's elite-level daily movement. Keep it going.`;
    if(totalLogged===0) return `Start logging today's metrics to unlock personalized behavior insights over time. The more you track, the smarter GRIND gets.`;
    return `You've logged ${totalLogged} of ${primaryMetrics.length} metrics today. Consistent logging unlocks trend analysis and personalized coaching as your history builds.`;
  };

  const CSS = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=DM+Sans:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    @keyframes slideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
    @keyframes pop { 0%{transform:scale(1)} 50%{transform:scale(1.18)} 100%{transform:scale(1)} }

    /* App shell */
    .app{display:flex;flex-direction:column;min-height:100vh;max-width:430px;margin:0 auto;background:${T.bg};position:relative;}
    .fi{flex:1;overflow-y:auto;padding-bottom:100px;-webkit-overflow-scrolling:touch;}
    .fi::-webkit-scrollbar{display:none;}

    /* Tab bar */
    .tab-bar{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:${T.bgCard};border-top:1px solid ${T.border};display:flex;padding:8px 4px calc(8px + env(safe-area-inset-bottom));z-index:100;backdrop-filter:blur(12px);}
    .tb{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;background:none;border:none;cursor:pointer;padding:4px 2px;position:relative;min-width:0;}
    .tb-dot{width:4px;height:4px;border-radius:50%;background:${T.accent};opacity:0;transition:opacity .2s;margin-bottom:1px;}
    .tb.active .tb-dot{opacity:1;}
    .tb-l{font-family:${T.fb};font-size:9px;font-weight:600;color:${T.textMuted};letter-spacing:.3px;white-space:nowrap;transition:color .2s;overflow:hidden;text-overflow:ellipsis;max-width:100%;}
    .tb.active .tb-l{color:${T.accent};}

    /* Buttons */
    .btn-primary{width:100%;padding:16px;border:none;border-radius:16px;background:${T.accent};color:#fff;font-family:${T.fb};font-size:16px;font-weight:700;cursor:pointer;transition:all .2s;letter-spacing:.2px;}
    .btn-primary:hover{opacity:.92;}
    .btn-secondary{width:100%;padding:15px;border:1.5px solid ${T.border};border-radius:16px;background:transparent;color:${T.text};font-family:${T.fb};font-size:16px;font-weight:600;cursor:pointer;transition:all .2s;}
    .btn-secondary:hover{border-color:${T.borderMid};}

    /* Input */
    .input-field{width:100%;background:${T.bg};border:1.5px solid ${T.border};border-radius:12px;padding:14px 16px;font-size:16px;color:${T.text};outline:none;font-family:${T.fb};transition:border-color .2s;}
    .input-field:focus{border-color:${T.accent};}
    .input-field::placeholder{color:${T.textMuted};}

    /* Section */
    .section{padding:0 20px;margin-bottom:6px;}
    .sh{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
    .sh-t{font-family:${T.fd};font-size:20px;font-weight:700;color:${T.text};}
    .sh-s{font-size:11px;color:${T.textMuted};letter-spacing:1px;font-weight:600;cursor:pointer;}
    .sh-s:hover{color:${T.accent};}

    /* Score */
    .score-card{margin:0 20px 16px;padding:24px;background:${T.bgCard};border:1px solid ${T.border};border-radius:24px;position:relative;overflow:hidden;}
    .score-card::after{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,${T.accent},${T.teal});}

    /* Reflect */
    .r-ta{width:100%;background:${T.bg};border:1.5px solid ${T.border};border-radius:12px;padding:14px;font-size:15px;color:${T.text};outline:none;resize:none;line-height:1.6;transition:border-color .2s;}
    .r-ta:focus{border-color:${T.accent};}
    .r-ta::placeholder{color:${T.textMuted};}
    .scale-b{flex:1;padding:11px 0;border-radius:10px;border:1.5px solid ${T.border};background:${T.bg};font-family:${T.fb};font-size:16px;font-weight:600;color:${T.textSub};cursor:pointer;transition:all .15s;text-align:center;}
    .scale-b:hover{border-color:${T.borderMid};}
    .scale-b.on{border-color:${T.accent};background:${T.accentLight};color:${T.accent};}

    /* Pill sub-tabs */
    .sub-tabs{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;padding-bottom:2px;margin-bottom:18px;}
    .sub-tabs::-webkit-scrollbar{display:none;}

    /* Week strip */
    .week-strip{display:flex;gap:6px;padding:0 20px 20px;overflow-x:auto;scrollbar-width:none;}
    .week-strip::-webkit-scrollbar{display:none;}
    .day-chip{flex:1;min-width:44px;background:${T.bgCard};border:1px solid ${T.border};border-radius:14px;padding:10px 4px;text-align:center;cursor:pointer;transition:all .2s;}
    .day-chip.active{background:${T.accent};border-color:${T.accent};}
    .day-chip:hover:not(.active){border-color:${T.borderMid};}

    /* Modals */
    .num-in{background:${T.bg};border:1.5px solid ${T.border};border-radius:14px;padding:18px;font-family:${T.fd};font-size:38px;font-weight:700;color:${T.text};text-align:center;width:100%;outline:none;transition:border-color .2s;}
    .num-in:focus{border-color:${T.accent};}
    .spin-b{width:46px;height:46px;background:${T.bg};border:1.5px solid ${T.border};border-radius:50%;font-size:22px;cursor:pointer;color:${T.text};transition:all .15s;display:flex;align-items:center;justify-content:center;}
    .spin-b:hover{background:${T.accent};border-color:${T.accent};color:#fff;}
    .save-btn{width:100%;padding:15px;border:none;border-radius:14px;font-family:${T.fb};font-size:17px;font-weight:600;color:#fff;cursor:pointer;margin-top:14px;transition:all .2s;}
    .save-btn:hover{opacity:.9;}
    .cancel-lnk{display:block;text-align:center;font-size:14px;color:${T.textMuted};cursor:pointer;margin-top:12px;padding:8px;}
    .cancel-lnk:hover{color:${T.text};}
    .form-lbl{font-size:11px;font-weight:600;letter-spacing:1.5px;color:${T.textMuted};margin-bottom:8px;display:block;text-transform:uppercase;}
    .form-grp{margin-bottom:18px;}
    .txt-in{background:${T.bg};border:1.5px solid ${T.border};border-radius:12px;padding:14px 16px;font-size:16px;font-weight:500;color:${T.text};width:100%;outline:none;transition:border-color .2s;}
    .txt-in:focus{border-color:${T.accent};}
    .time-in{background:${T.bg};border:1.5px solid ${T.border};border-radius:12px;padding:12px 16px;font-family:${T.fd};font-size:28px;font-weight:700;color:${T.text};width:100%;outline:none;transition:border-color .2s;}
    .time-in:focus{border-color:${T.accent};}
    .day-tog{flex:1;padding:10px 3px;border-radius:10px;border:1.5px solid ${T.border};background:${T.bg};font-size:10px;letter-spacing:.5px;color:${T.textSub};cursor:pointer;text-align:center;transition:all .15s;font-weight:700;}
    .day-tog.on{background:${T.accentLight};border-color:${T.accent};color:${T.accent};}

    /* Wellness card info */
    .info-box{background:${T.bgMuted};border-radius:14px;padding:16px;margin-bottom:12px;}
    .info-lbl{font-size:10px;font-weight:700;letter-spacing:2px;color:${T.textMuted};margin-bottom:10px;text-transform:uppercase;}

    /* Phase chip */
    .chip{display:inline-flex;align-items:center;gap:6px;border-radius:20px;padding:5px 12px;font-size:12px;font-weight:600;}

    .symptom-chip{padding:8px 14px;border-radius:20px;border:1.5px solid ${T.border};background:${T.bgCard};font-size:13px;color:${T.textSub};cursor:pointer;transition:all .15s;display:inline-flex;align-items:center;gap:5px;}
    .symptom-chip.on{border-color:${T.accent};background:${T.accentLight};color:${T.accent};}
    .act-chip{padding:7px 14px;border-radius:20px;border:1.5px solid ${T.border};background:${T.bgCard};font-size:13px;color:${T.textSub};cursor:pointer;transition:all .15s;}
    .act-chip.on{border-color:${T.teal};background:${T.tealLight};color:${T.teal};}
    .add-btn{width:100%;padding:14px;background:transparent;border:1.5px dashed ${T.border};border-radius:14px;font-size:14px;font-weight:600;color:${T.textMuted};cursor:pointer;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:14px;letter-spacing:.5px;}
    .add-btn:hover{border-color:${T.accent};color:${T.accent};background:${T.accentLight};}
    .toggle{width:42px;height:24px;border-radius:12px;border:none;cursor:pointer;position:relative;transition:all .25s;flex-shrink:0;}
    .toggle-k{position:absolute;top:3px;width:18px;height:18px;border-radius:50%;background:#fff;transition:left .25s;box-shadow:0 1px 4px rgba(0,0,0,.15);}

    /* Dev conn */
    .dev-conn{background:${T.bgCard};border:1.5px solid ${T.border};border-radius:18px;padding:20px;margin-bottom:10px;cursor:pointer;transition:all .2s;}
    .dev-conn:hover{border-color:${T.borderMid};}
    .dev-conn.on{border-color:#90CEC3;background:${T.tealLight};}

    /* Trend card */
    .trend-card{background:${T.bgCard};border:1px solid ${T.border};border-radius:16px;padding:18px;margin-bottom:10px;cursor:pointer;transition:all .2s;}
    .trend-card:hover{border-color:${T.borderMid};box-shadow:0 4px 12px rgba(26,23,20,.05);}

    /* Meditation card */
    .med-card{background:${T.bgCard};border:1px solid ${T.border};border-radius:18px;overflow:hidden;cursor:pointer;transition:all .2s;margin-bottom:12px;}
    .med-card:hover{box-shadow:0 6px 20px rgba(26,23,20,.08);transform:translateY(-2px);}
  `;

  // ════════════════════════════════════════════════════
  // SPLASH SCREEN
  // ════════════════════════════════════════════════════
  if(screen==="splash") return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(160deg,#1A1714 0%,#2A2320 40%,#3A2E28 100%)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@900&display=swap');
        @keyframes grindFade {
          0%   { opacity:0; transform:scale(.92) translateY(8px); }
          30%  { opacity:1; transform:scale(1) translateY(0); }
          70%  { opacity:1; transform:scale(1) translateY(0); }
          100% { opacity:0; transform:scale(1.04) translateY(-6px); }
        }
        @keyframes tagFade {
          0%,20% { opacity:0; }
          40%    { opacity:1; }
          70%    { opacity:1; }
          100%   { opacity:0; }
        }
        .splash-word { animation: grindFade 2.2s ease forwards; }
        .splash-tag  { animation: tagFade 2.2s ease forwards; }
      `}</style>
      <div style={{ textAlign:"center" }}>
        <div className="splash-word" style={{ fontFamily:"'Playfair Display',serif", fontSize:84, fontWeight:900, color:"#fff", letterSpacing:-5, lineHeight:1 }}>GRIND</div>
        <div className="splash-tag" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:500, letterSpacing:5, color:"rgba(255,255,255,.45)", marginTop:10, textTransform:"uppercase" }}>Performance OS</div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════
  // WELCOME SCREEN
  // ════════════════════════════════════════════════════
  if(screen==="welcome") return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(160deg,#1A1714 0%,#2A2320 40%,#3A2E28 100%)`, display:"flex", flexDirection:"column", position:"relative", overflow:"hidden" }}>
      <style>{CSS}</style>
      {/* Decorative circles */}
      <div style={{ position:"absolute", top:-80, right:-80, width:300, height:300, borderRadius:"50%", background:`radial-gradient(circle,${T.accent}22,transparent 70%)`, pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:100, left:-60, width:250, height:250, borderRadius:"50%", background:`radial-gradient(circle,${T.teal}18,transparent 70%)`, pointerEvents:"none" }}/>
      <div style={{ position:"absolute", top:"35%", left:"50%", transform:"translateX(-50%)", width:1, height:160, background:`linear-gradient(180deg,transparent,${T.accent}66,transparent)`, pointerEvents:"none" }}/>

      {/* Top strip */}
      <div style={{ padding:"56px 28px 0", animation:"fadeIn .8s ease forwards" }}>
        <div style={{ fontFamily:T.fd, fontSize:11, fontWeight:600, letterSpacing:6, color:T.accent, textTransform:"uppercase", marginBottom:32, opacity:.8 }}>Wellness · Performance · Balance</div>
        <div style={{ fontFamily:T.fd, fontSize:88, fontWeight:900, color:"#fff", lineHeight:.9, letterSpacing:-5 }}>GRIND</div>
        <div style={{ fontFamily:T.fd, fontSize:26, fontWeight:400, color:"rgba(255,255,255,.55)", letterSpacing:1, marginTop:12, fontStyle:"italic" }}>Your performance OS</div>
      </div>

      {/* Center tagline */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"center", padding:"0 28px" }}>
        <div style={{ fontFamily:T.fb, fontSize:18, color:"rgba(255,255,255,.65)", lineHeight:1.9, maxWidth:340 }}>
          Track every metric that matters. Understand your body. Move through every season of health — with confidence.
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:32 }}>
          {["Wearable sync — Apple Watch, WHOOP, Garmin", "Cycle tracking with phase intelligence", "AI wellness coaching, 24/7", "Guided meditations under 15 minutes"].map((f,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, animation:`fadeIn ${.6+i*.15}s ease forwards`, opacity:0, animationFillMode:"forwards" }}>
              <div style={{ width:20, height:20, borderRadius:"50%", background:T.accent, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <div style={{ width:8, height:8, border:"2px solid #fff", borderTop:"none", borderLeft:"none", transform:"rotate(45deg) translate(-1px,-2px)" }}/>
              </div>
              <span style={{ fontFamily:T.fb, fontSize:16, color:"rgba(255,255,255,.75)", fontWeight:400 }}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={{ padding:"0 24px 56px" }}>
        <button onClick={()=>{setAuthMode("signup");setScreen("auth");}} className="btn-primary" style={{ marginBottom:12, fontSize:17, fontFamily:T.fb, fontWeight:600, letterSpacing:.3 }}>
          Get Started — 100% Free
        </button>
        <button onClick={()=>{setAuthMode("login");setScreen("auth");}} className="btn-secondary" style={{ background:"rgba(255,255,255,.06)", borderColor:"rgba(255,255,255,.15)", color:"rgba(255,255,255,.6)" }}>
          I already have an account
        </button>
        <div style={{ fontFamily:T.fb, fontSize:11, color:"rgba(255,255,255,.3)", textAlign:"center", marginTop:16, lineHeight:1.6 }}>
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════
  // AUTH SCREEN
  // ════════════════════════════════════════════════════
  if(screen==="auth") return (
    <div className="auth-wrap fi">
      <style>{CSS}</style>
      <div style={{ padding:"48px 24px 24px" }}>
        <button onClick={()=>setScreen("welcome")} style={{ background:"none", border:"none", cursor:"pointer", color:T.textMuted, fontSize:14, fontFamily:T.fb, padding:0, marginBottom:32 }}>← Back</button>
        <div style={{ fontFamily:T.fd, fontSize:38, fontWeight:900, color:T.text, letterSpacing:-2, marginBottom:4 }}>GRIND</div>
        <div style={{ fontFamily:T.fb, fontSize:14, color:T.textMuted, marginBottom:32 }}>
          {authMode==="signup" ? "Create your account to get started." : "Welcome back. Sign in to continue."}
        </div>

        {authMode==="signup" && (
          <div style={{ marginBottom:16 }}>
            <div className="form-lbl">Your Name</div>
            <input className="input-field" placeholder="First name" value={authForm.name} onChange={e=>setAuthForm(f=>({...f,name:e.target.value}))}/>
          </div>
        )}
        <div style={{ marginBottom:16 }}>
          <div className="form-lbl">Email Address</div>
          <input className="input-field" type="email" placeholder="you@example.com" value={authForm.email} onChange={e=>setAuthForm(f=>({...f,email:e.target.value}))}/>
        </div>
        <div style={{ marginBottom:authMode==="signup"?16:24 }}>
          <div className="form-lbl">Password</div>
          <input className="input-field" type="password" placeholder="At least 8 characters" value={authForm.password} onChange={e=>setAuthForm(f=>({...f,password:e.target.value}))}/>
        </div>
        {authMode==="signup" && (
          <div style={{ marginBottom:24 }}>
            <div className="form-lbl">Confirm Password</div>
            <input className="input-field" type="password" placeholder="Repeat your password" value={authForm.confirmPassword} onChange={e=>setAuthForm(f=>({...f,confirmPassword:e.target.value}))}/>
          </div>
        )}
        {authErr && <div style={{ background:"#FEF0EE", border:"1px solid #F5C0B4", borderRadius:10, padding:"10px 14px", fontFamily:T.fb, fontSize:13, color:"#C0341A", marginBottom:16 }}>{authErr}</div>}

        {authMode==="signup" && (
          <div style={{ marginBottom:24 }}>
            <div className="form-lbl" style={{ marginBottom:10 }}>Enable Cycle Tracking?</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setIsFemale(true)} style={{ flex:1,padding:12,borderRadius:12,border:`1.5px solid ${isFemale?T.rose:T.border}`,background:isFemale?T.roseLight:T.bg,fontFamily:T.fb,fontSize:14,color:isFemale?T.rose:T.textSub,cursor:"pointer",fontWeight:500 }}>Yes, enable it</button>
              <button onClick={()=>setIsFemale(false)} style={{ flex:1,padding:12,borderRadius:12,border:`1.5px solid ${!isFemale?T.teal:T.border}`,background:!isFemale?T.tealLight:T.bg,fontFamily:T.fb,fontSize:14,color:!isFemale?T.teal:T.textSub,cursor:"pointer",fontWeight:500 }}>Skip for now</button>
            </div>
          </div>
        )}

        <button className="btn-primary" onClick={()=>{
          if(authMode==="signup") {
            if(!authForm.name.trim()) return setAuthErr("Please enter your name.");
            if(!authForm.email.includes("@")) return setAuthErr("Please enter a valid email.");
            if(authForm.password.length<8) return setAuthErr("Password must be at least 8 characters.");
            if(authForm.password!==authForm.confirmPassword) return setAuthErr("Passwords do not match.");
            setUser({ name:authForm.name, email:authForm.email });
            setAuthErr("");
            setScreen("tutorial");
          } else {
            if(!authForm.email||!authForm.password) return setAuthErr("Please enter your email and password.");
            setUser({ name:authForm.name||"Friend", email:authForm.email });
            setAuthErr("");
            setScreen("app");
          }
        }}>
          {authMode==="signup" ? "Create My Account" : "Sign In"}
        </button>

        <div style={{ textAlign:"center", marginTop:20, fontFamily:T.fb, fontSize:14, color:T.textMuted }}>
          {authMode==="signup" ? "Already have an account? " : "Don't have an account? "}
          <span style={{ color:T.accent, fontWeight:600, cursor:"pointer" }} onClick={()=>{setAuthMode(authMode==="signup"?"login":"signup");setAuthErr("");}}>
            {authMode==="signup" ? "Sign in" : "Create one"}
          </span>
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════
  // TUTORIAL
  // ════════════════════════════════════════════════════
  if(screen==="tutorial") {
    const step = TUTORIAL_STEPS[tutorialStep];
    const isLast = tutorialStep === TUTORIAL_STEPS.length-1;
    return (
      <div style={{ minHeight:"100vh", background:T.bgCard, display:"flex", flexDirection:"column" }} className="fi">
        <style>{CSS}</style>
        {/* Progress */}
        <div style={{ padding:"48px 24px 0" }}>
          <div style={{ display:"flex", gap:6, marginBottom:36 }}>
            {TUTORIAL_STEPS.map((_,i)=>(
              <div key={i} style={{ flex:1, height:3, borderRadius:2, background:i<=tutorialStep?T.accent:T.bg, transition:"background .3s" }}/>
            ))}
          </div>
          <div style={{ fontFamily:T.fb, fontSize:12, fontWeight:600, letterSpacing:2, color:T.textMuted, textTransform:"uppercase", marginBottom:8 }}>
            Step {tutorialStep+1} of {TUTORIAL_STEPS.length}
          </div>
          <div style={{ fontFamily:T.fd, fontSize:34, fontWeight:900, color:T.text, letterSpacing:-1, lineHeight:1.1, marginBottom:16 }} key={tutorialStep}>
            {step.title}
          </div>
          <div style={{ fontFamily:T.fb, fontSize:16, color:T.textSub, lineHeight:1.75, marginBottom:28 }}>
            {step.body}
          </div>

          {/* Visual placeholder card */}
          <div style={{ background:T.bg, borderRadius:20, padding:24, marginBottom:24, border:`1px solid ${T.border}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:T.accent+"18", border:`1px solid ${T.accent}30`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:T.accent }}>{tutorialStep+1}</div>
              </div>
              <div>
                <div style={{ fontFamily:T.fd, fontSize:16, fontWeight:700, color:T.text }}>{step.icon}</div>
                <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted }}>Feature preview</div>
              </div>
            </div>
            <div style={{ height:6, background:T.border, borderRadius:3, marginBottom:8, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${(tutorialStep+1)/TUTORIAL_STEPS.length*100}%`, background:`linear-gradient(90deg,${T.accent},${T.teal})`, borderRadius:3 }}/>
            </div>
            <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, fontStyle:"italic" }}>
              Tip: {step.tip}
            </div>
          </div>
        </div>

        <div style={{ flex:1 }}/>
        <div style={{ padding:"0 24px 48px" }}>
          <button className="btn-primary" onClick={()=>{ if(isLast){setScreen("app");}else{setTutorialStep(t=>t+1);} }}>
            {isLast ? "Start Using GRIND" : "Next"}
          </button>
          {!isLast && (
            <button className="btn-secondary" style={{ marginTop:10 }} onClick={()=>setScreen("app")}>Skip Tutorial</button>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // MAIN APP
  // ════════════════════════════════════════════════════
  const GREET_MSG = () => { const h=new Date().getHours(); if(h<12)return"Good morning"; if(h<17)return"Good afternoon"; return"Good evening"; };

  const tabs = [
    {id:"today",label:"Today"},
    ...(isFemale?[{id:"cycle",label:"Cycle"}]:[]),
    {id:"meditate",label:"Meditate"},
    {id:"insights",label:"Insights"},
    {id:"reflect",label:"Reflect"},
    {id:"support",label:"Support"},
    {id:"devices",label:"Devices"},
    {id:"profile",label:"Profile"},
  ];

  return (
    <div style={{ background:T.bg }}>
      <style>{CSS}</style>
      <div className="app">

        {/* ── HEADER ── */}
        <div style={{ padding:"52px 20px 16px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, fontWeight:500, marginBottom:2 }}>{GREET_MSG()},</div>
              <div style={{ fontFamily:T.fd, fontSize:32, fontWeight:900, color:T.text, letterSpacing:-1, lineHeight:1 }}>{profile?.name || user?.name || "Friend"}</div>
            </div>
            <button onClick={()=>setTab("profile")} style={{ width:44,height:44,borderRadius:"50%",background:`linear-gradient(135deg,${T.accent},${T.teal})`,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:900, color:"#fff" }}>{(profile?.name||user?.name||"G").charAt(0).toUpperCase()}</div>
            </button>
          </div>
          {isFemale && cycleDay && (
            <div className="chip" style={{ background:phase.light, color:phase.color, border:`1px solid ${phase.color}40`, marginTop:10, cursor:"pointer" }} onClick={()=>setTab("cycle")}>
              {phase.name} Phase  ·  Day {cycleDay}
            </div>
          )}
        </div>

        {/* ── WEEK STRIP ── */}
        {tab!=="cycle"&&tab!=="support"&&tab!=="devices"&&(
          <div className="week-strip">
            {WEEK.map(day=>{
              const d=new Date(day+"T12:00:00"); const filled=Object.keys(logData[day]||{}).length>0;
              return (
                <div key={day} className={`day-chip ${selectedDate===day?"active":""}`} onClick={()=>{setSelectedDate(day);setTab("today");}}>
                  <div style={{ fontFamily:T.fb, fontSize:9, fontWeight:600, letterSpacing:1, color:selectedDate===day?"rgba(255,255,255,.7)":T.textMuted }}>{day===today()?"NOW":DAY_NAMES[d.getDay()]}</div>
                  <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:selectedDate===day?"#fff":T.textSub, lineHeight:1.3 }}>{d.getDate()}</div>
                  <div style={{ width:4, height:4, borderRadius:2, margin:"4px auto 0", background:filled?(selectedDate===day?"rgba(255,255,255,.5)":T.accent):"transparent" }}/>
                </div>
              );
            })}
          </div>
        )}

        {/* ════ TODAY ════ */}
        {tab==="today"&&(
          <div className="fi">
            {/* Score card */}
            <div className="score-card">
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontFamily:T.fb, fontSize:11, fontWeight:600, letterSpacing:2, color:T.textMuted, textTransform:"uppercase" }}>Daily Score</div>
                  <div style={{ fontFamily:T.fd, fontSize:52, fontWeight:900, color:T.text, letterSpacing:-3, lineHeight:1 }}>
                    {Math.round((totalLogged/primaryMetrics.length)*100)}<span style={{ fontSize:22, color:T.textMuted }}>%</span>
                  </div>
                  <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginTop:4 }}>
                    {totalLogged===0?"Start logging your day":totalLogged<5?"Building momentum":totalLogged<9?"Looking strong":"Perfect day — keep it up!"}
                  </div>
                </div>
                <Radial value={totalLogged} max={primaryMetrics.length} color={T.accent} size={88} stroke={7}>
                  <div style={{ fontFamily:T.fd, fontWeight:900, fontSize:20, color:T.text }}>{totalLogged}</div>
                  <div style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted }}>/{primaryMetrics.length}</div>
                </Radial>
              </div>
              <div style={{ display:"flex", gap:8, marginTop:18 }}>
                {[
                  {label:"Recovery", val:todayData.recovery?`${todayData.recovery}%`:"—", color:T.green},
                  {label:"HRV", val:todayData.hrv?`${todayData.hrv}ms`:"—", color:T.teal},
                  {label:"Sleep", val:todayData.sleep?`${todayData.sleep}h`:"—", color:"#7060C8"},
                ].map(s=>(
                  <div key={s.label} style={{ flex:1, padding:12, background:T.bgMuted, borderRadius:12, textAlign:"center" }}>
                    <div style={{ fontFamily:T.fd, fontSize:17, fontWeight:700, color:s.color }}>{s.val}</div>
                    <div style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted, fontWeight:600, letterSpacing:.5, marginTop:2 }}>{s.label.toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cycle banner */}
            {isFemale && (
              <div className="section" style={{ marginBottom:20 }}>
                <div onClick={()=>setTab("cycle")} style={{ background:phase.light, border:`1px solid ${phase.color}40`, borderRadius:18, padding:18, cursor:"pointer", display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:50, height:50, borderRadius:14, background:phase.color+"20", border:`1px solid ${phase.color}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <div style={{ fontFamily:T.fd, fontSize:11, fontWeight:700, color:phase.color, textAlign:"center", lineHeight:1.2 }}>{phase.name.slice(0,3).toUpperCase()}</div>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:T.fd, fontSize:16, fontWeight:700, color:phase.color }}>{phase.name} Phase — Day {cycleDay}</div>
                    <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, marginTop:3, lineHeight:1.45 }}>{phase.description.slice(0,80)}…</div>
                    <div style={{ fontFamily:T.fb, fontSize:12, color:phase.color, marginTop:6, fontWeight:500 }}>Next period in ~{daysUntilNext} days  ·  Tap to explore</div>
                  </div>
                </div>
              </div>
            )}

            {/* Bio Signals */}
            <div className="section" style={{ marginBottom:24 }}>
              <div className="sh"><div className="sh-t">Bio Signals</div><div className="sh-s">FROM DEVICE</div></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                {["hrv","rhr","spo2"].map(key=>{
                  const m=INSIGHTS[key]; const val=todayData[key]; const rng=val?getRange(key,val):null;
                  return (
                    <div key={key} style={{ background:T.bgCard, border:`1px solid ${connectedMetrics.includes(key)?T.teal+"44":T.border}`, borderRadius:16, padding:14, cursor:"pointer", transition:"all .2s" }}
                      onClick={()=>setActiveMetric(key)}>
                      <div style={{ fontFamily:T.fb, fontSize:9, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:6 }}>{m.label}</div>
                      <div style={{ fontFamily:T.fd, fontSize:22, fontWeight:700, color:rng?.color||T.text, lineHeight:1 }}>{val||"—"}</div>
                      <div style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted, marginTop:2 }}>{m.unit}</div>
                      {rng && <Badge color={rng.color} style={{ marginTop:6 }}>{rng.label}</Badge>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {["strain","recovery"].map(key=>{
                  const m=INSIGHTS[key]; const val=todayData[key]; const rng=val?getRange(key,val):null;
                  return (
                    <div key={key} style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:16, padding:16, cursor:"pointer" }} onClick={()=>setActiveMetric(key)}>
                      <div style={{ fontFamily:T.fb, fontSize:9, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:8 }}>{m.label}</div>
                      <div style={{ fontFamily:T.fd, fontSize:26, fontWeight:700, color:rng?.color||T.text, letterSpacing:-1 }}>{val||"—"}<span style={{ fontSize:12, color:T.textMuted }}> {m.unit}</span></div>
                      {val && <div style={{ height:3, background:T.bgMuted, borderRadius:2, overflow:"hidden", marginTop:10 }}><div style={{ height:"100%", width:`${Math.min((val/m.max)*100,100)}%`, background:rng?.color||m.color, borderRadius:2 }}/></div>}
                      {rng && <div style={{ fontFamily:T.fb, fontSize:11, color:rng.color, marginTop:6, fontWeight:500 }}>{rng.label}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Daily Log */}
            <div className="section" style={{ marginBottom:24 }}>
              <div className="sh"><div className="sh-t">Daily Log</div><div className="sh-s">TAP TO UPDATE</div></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {["sleep","steps","calories","water","mood","weight","reading","mindfulness"].map(key=>{
                  const m=INSIGHTS[key]; const val=todayData[key]; const hasVal=val!==undefined;
                  const pct=hasVal?Math.min((val/m.max)*100,100):0; const rng=hasVal?getRange(key,val):null;
                  const synced=connectedMetrics.includes(key);
                  return (
                    <div key={key} className={`mc ${synced?"synced":""} ${celebrateKey===key?"celebrate":""}`}
                      onClick={()=>{setEditMetric(key);setInputVal(hasVal?String(val):"");}}>
                      <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:hasVal?`linear-gradient(90deg,${rng?.color||m.color},transparent)`:T.border, opacity:hasVal?.8:.4, borderRadius:"20px 20px 0 0" }}/>
                      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                        <div style={{ fontFamily:T.fb, fontSize:9, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase" }}>{m.label}</div>
                        {synced && <div style={{ fontSize:10, color:T.teal, fontWeight:600 }}>Synced</div>}
                      </div>
                      <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:700, lineHeight:1, letterSpacing:-1, color:hasVal?(rng?.color||m.color):T.textMuted }}>
                        {key==="mood"&&hasVal ? MOODS[val]||val : hasVal ? val : "—"}
                      </div>
                      <div style={{ fontFamily:T.fb, fontSize:11, color:T.textMuted, marginTop:3 }}>{hasVal?m.unit:"Tap to log"}</div>
                      {hasVal && <div style={{ height:2, background:T.bgMuted, borderRadius:1, marginTop:12, overflow:"hidden" }}><div style={{ height:"100%", width:`${pct}%`, background:rng?.color||m.color, borderRadius:1, transition:"width .8s ease" }}/></div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Suggested Meditation */}
            <div className="section" style={{ marginBottom:24 }}>
              <div className="sh"><div className="sh-t">Today's Meditation</div><div className="sh-s" onClick={()=>setTab("meditate")}>SEE ALL</div></div>
              {(()=>{
                const moodType = MOOD_MED[todayData.mood||3]||"calming";
                const phaseType = isFemale ? phase.meditationMood : moodType;
                const list = MEDITATIONS[phaseType]||MEDITATIONS.calming;
                return list.slice(0,2).map((med,i)=>(
                  <div key={i} onClick={()=>setSelectedMeditation(med)}
                    style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:16, padding:16, marginBottom:10, cursor:"pointer", display:"flex", gap:14, alignItems:"center", transition:"all .2s" }}>
                    <div style={{ width:52, height:52, borderRadius:14, background:T.accentLight, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <div style={{ fontFamily:T.fd, fontSize:11, fontWeight:700, color:T.accent, textAlign:"center", lineHeight:1.3 }}>PLAY</div>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:T.fd, fontSize:15, fontWeight:700, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{med.title}</div>
                      <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, marginTop:2 }}>{med.channel}  ·  {med.duration}</div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Screen Time */}
            <div className="section" style={{ marginBottom:24 }}>
              <div className="sh"><div className="sh-t">Screen Time</div><div className="sh-s">TODAY · EST.</div></div>
              <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:20, padding:20 }}>
                {(()=>{const total=SCREEN_TIME.reduce((a,b)=>a+b.mins,0);const h=Math.floor(total/60),m2=total%60;return(<>
                  <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:16 }}>
                    <div style={{ fontFamily:T.fd, fontWeight:900, fontSize:38, color:T.text, letterSpacing:-2 }}>{h}<span style={{ fontSize:18, fontWeight:400 }}>h</span> {m2}<span style={{ fontSize:18, fontWeight:400 }}>m</span></div>
                    <Badge color={total>240?T.rose:T.green}>{total>300?"High usage":total>180?"Moderate":"Healthy"}</Badge>
                  </div>
                  {SCREEN_TIME.map(app=>(<div key={app.name} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                    <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, width:110, flexShrink:0 }}>{app.name}</div>
                    <div style={{ flex:1, height:6, background:T.bgMuted, borderRadius:3, overflow:"hidden" }}><div style={{ height:"100%", width:`${(app.mins/total)*100}%`, background:app.color, borderRadius:3 }}/></div>
                    <div style={{ fontFamily:T.fb, fontSize:12, fontWeight:600, color:T.textMuted, width:34, textAlign:"right" }}>{app.mins}m</div>
                  </div>))}
                  <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, marginTop:14, lineHeight:1.7, fontStyle:"italic", padding:"12px 14px", background:T.bgMuted, borderRadius:10 }}>
                    Research links social media use over 90 min/day with increased anxiety risk. Even a small daily reduction can shift your mood score within two weeks.
                  </div>
                </>);})()}
              </div>
            </div>
          </div>
        )}

        {/* ════ CYCLE ════ */}
        {tab==="cycle"&&isFemale&&(
          <div className="fi">
            <div style={{ padding:"8px 20px 0" }}>
              <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1, marginBottom:4 }}>Cycle Tracker</div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginBottom:18 }}>Syncs with Flo and Clue</div>
              <div className="sub-tabs">
                {["Overview","Phases","Nutrition","Activities","Log"].map(t=>(
                  <PillBtn key={t} active={cycleTab===t.toLowerCase()} onClick={()=>setCycleTab(t.toLowerCase())}>{t}</PillBtn>
                ))}
              </div>

              {cycleTab==="overview"&&(<>
                <div style={{ background:phase.light, border:`1px solid ${phase.color}30`, borderRadius:20, padding:22, marginBottom:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div>
                      <div style={{ fontFamily:T.fb, fontSize:11, fontWeight:600, letterSpacing:2, color:phase.color, textTransform:"uppercase", marginBottom:4 }}>Current Phase</div>
                      <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1 }}>{phase.name}</div>
                      <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, marginTop:2 }}>{phase.days} — Day {cycleDay} of {cycleData.cycleLength}</div>
                    </div>
                    <div style={{ background:T.bgCard, borderRadius:16, padding:"12px 16px", textAlign:"center", border:`1px solid ${phase.color}30` }}>
                      <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:phase.color, lineHeight:1 }}>{daysUntilNext}</div>
                      <div style={{ fontFamily:T.fb, fontSize:9, color:T.textMuted, letterSpacing:1, fontWeight:600 }}>DAYS TO<br/>NEXT</div>
                    </div>
                  </div>
                  <div style={{ fontFamily:T.fb, fontSize:14, color:T.textSub, lineHeight:1.7, marginTop:14 }}>{phase.description}</div>

                  {/* Cycle ring */}
                  <div style={{ marginTop:18 }}>
                    <div style={{ fontFamily:T.fb, fontSize:9, fontWeight:600, letterSpacing:2, color:T.textMuted, marginBottom:8, textTransform:"uppercase" }}>Your {cycleData.cycleLength}-day cycle</div>
                    <div style={{ display:"flex", gap:3, alignItems:"flex-end" }}>
                      {Array.from({length:cycleData.cycleLength},(_,i)=>{
                        const d=i+1, ph=d<=cycleData.periodLength?"menstrual":d<=13?"follicular":d<=17?"ovulatory":"luteal";
                        const phD=CYCLE_PHASES[ph]; const isActive=d===cycleDay;
                        return <div key={d} style={{ flex:1, height:isActive?20:8, background:isActive?phD.color:phD.color+"55", borderRadius:isActive?4:2, transition:"all .2s", boxShadow:isActive?`0 0 6px ${phD.color}66`:"none" }}/>;
                      })}
                    </div>
                    <div style={{ display:"flex", gap:14, marginTop:8 }}>
                      {Object.entries(CYCLE_PHASES).map(([k,p])=>(
                        <div key={k} style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <div style={{ width:8, height:8, borderRadius:2, background:p.color }}/>
                          <span style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted }}>{p.name.slice(0,3)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                  <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:14, padding:16 }}>
                    <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:6 }}>Energy</div>
                    <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:phase.color }}>{phase.energy}</div>
                  </div>
                  <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:14, padding:16 }}>
                    <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:6 }}>Mood</div>
                    <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:phase.color }}>{phase.mood}</div>
                  </div>
                </div>
                <div className="info-box">
                  <div className="info-lbl">Emotional Landscape</div>
                  {phase.emotional.map((e,i)=><div key={i} style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, padding:"5px 0", borderBottom:i<phase.emotional.length-1?`1px solid ${T.border}`:"none", lineHeight:1.5 }}>— {e}</div>)}
                </div>
                <button onClick={()=>setCycleTab("log")} style={{ width:"100%", padding:14, background:phase.color, border:"none", borderRadius:14, fontFamily:T.fb, fontWeight:600, fontSize:15, color:"#fff", cursor:"pointer", marginBottom:20 }}>
                  Log Today's Symptoms
                </button>
              </>)}

              {cycleTab==="phases"&&(<>
                {Object.entries(CYCLE_PHASES).map(([key,p])=>(
                  <div key={key} style={{ background:p.light, border:`1px solid ${p.color}30`, borderRadius:18, padding:20, marginBottom:12 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                      <div>
                        <div style={{ fontFamily:T.fd, fontSize:20, fontWeight:700, color:p.color }}>{p.name}</div>
                        <div style={{ fontFamily:T.fb, fontSize:12, color:T.textSub }}>{p.days}</div>
                      </div>
                      {currentPhase===key && <Badge color={p.color}>Current</Badge>}
                    </div>
                    <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, lineHeight:1.7 }}>{p.description}</div>
                  </div>
                ))}
              </>)}

              {cycleTab==="nutrition"&&(<>
                <div style={{ background:phase.light, border:`1px solid ${phase.color}30`, borderRadius:18, padding:18, marginBottom:14 }}>
                  <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:phase.color, marginBottom:4 }}>{phase.name} Phase — What to Eat</div>
                  <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, lineHeight:1.6 }}>Your nutritional needs shift with your cycle. Here's what your body needs right now.</div>
                </div>
                <div className="info-box">
                  <div className="info-lbl">Recommended Foods</div>
                  {phase.nutrition.map((n,i)=><div key={i} style={{ fontFamily:T.fb, fontSize:13, color:n.startsWith("Avoid")?T.rose:T.textSub, padding:"5px 0", borderBottom:i<phase.nutrition.length-1?`1px solid ${T.border}`:"none", lineHeight:1.5 }}>{n.startsWith("Avoid")?"Avoid —":"+"} {n.replace("Avoid: ","")}</div>)}
                </div>
                <div className="info-box">
                  <div className="info-lbl">Suggested Supplements</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {phase.supplements.map((s,i)=><div key={i} style={{ padding:"6px 14px", borderRadius:20, background:T.bgCard, border:`1px solid ${phase.color}40`, fontFamily:T.fb, fontSize:13, color:phase.color, fontWeight:500 }}>{s}</div>)}
                  </div>
                </div>
                <div style={{ background:T.bgMuted, borderRadius:14, padding:14, marginTop:4 }}>
                  <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, lineHeight:1.7 }}>Always consult your healthcare provider before starting new supplements, especially if you have underlying health conditions or take medications.</div>
                </div>
              </>)}

              {cycleTab==="activities"&&(<>
                <div style={{ background:phase.light, border:`1px solid ${phase.color}30`, borderRadius:18, padding:18, marginBottom:14 }}>
                  <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:phase.color }}>Best Activities This Phase</div>
                  <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, marginTop:4 }}>Energy: <span style={{ color:phase.color, fontWeight:600 }}>{phase.energy}</span></div>
                </div>
                <div className="info-box">
                  <div className="info-lbl">Recommended</div>
                  {phase.activities.map((a,i)=><div key={i} style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, padding:"5px 0", borderBottom:i<phase.activities.length-1?`1px solid ${T.border}`:"none", lineHeight:1.5 }}>{a}</div>)}
                </div>
                {phase.avoid.length>0 && <div className="info-box" style={{ borderLeft:`2px solid ${T.rose}` }}>
                  <div className="info-lbl" style={{ color:T.rose }}>Avoid or Modify</div>
                  {phase.avoid.map((a,i)=><div key={i} style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, padding:"4px 0" }}>{a}</div>)}
                </div>}
                <div className="info-box">
                  <div className="info-lbl">Physical Notes This Phase</div>
                  {phase.physical.map((p2,i)=><div key={i} style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, padding:"4px 0", borderBottom:i<phase.physical.length-1?`1px solid ${T.border}`:"none" }}>— {p2}</div>)}
                </div>
              </>)}

              {cycleTab==="log"&&(<>
                <div style={{ fontFamily:T.fb, fontSize:14, color:T.textSub, marginBottom:18, lineHeight:1.6 }}>Track symptoms and flow for today. This helps GRIND personalize your phase insights over time.</div>
                <div className="info-box">
                  <div className="info-lbl">Flow Level</div>
                  <div style={{ display:"flex", gap:8 }}>
                    {["Light","Moderate","Heavy","Spotting"].map(f=>(
                      <button key={f} onClick={()=>setFlowLevel(f)} style={{ flex:1, padding:"10px 4px", borderRadius:10, border:`1.5px solid ${flowLevel===f?T.rose:T.border}`, background:flowLevel===f?T.roseLight:T.bg, fontFamily:T.fb, fontSize:11, fontWeight:600, color:flowLevel===f?T.rose:T.textSub, cursor:"pointer", transition:"all .15s" }}>{f}</button>
                    ))}
                  </div>
                </div>
                <div className="info-box">
                  <div className="info-lbl">Symptoms</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {SYMPTOMS_LIST.map(s=>(
                      <div key={s} className={`symptom-chip ${todaySymptoms.includes(s)?"on":""}`} onClick={()=>setTodaySymptoms(prev=>prev.includes(s)?prev.filter(x=>x!==s):[...prev,s])}>{s}</div>
                    ))}
                  </div>
                </div>
                <div className="info-box">
                  <div className="info-lbl">Cycle Settings</div>
                  {[{label:"Cycle length (days)", key:"cycleLength", min:21, max:40},{label:"Period length (days)", key:"periodLength", min:2, max:10}].map(s=>(
                    <div key={s.key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                      <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub }}>{s.label}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <button onClick={()=>setCycleData(d=>({...d,[s.key]:Math.max(s.min,d[s.key]-1)}))} style={{ width:32, height:32, borderRadius:"50%", border:`1px solid ${T.border}`, background:T.bg, fontSize:18, cursor:"pointer", color:T.text, display:"flex", alignItems:"center", justifyContent:"center" }}>−</button>
                        <div style={{ fontFamily:T.fd, fontSize:22, fontWeight:700, color:T.text, width:36, textAlign:"center" }}>{cycleData[s.key]}</div>
                        <button onClick={()=>setCycleData(d=>({...d,[s.key]:Math.min(s.max,d[s.key]+1)}))} style={{ width:32, height:32, borderRadius:"50%", border:`1px solid ${T.border}`, background:T.bg, fontSize:18, cursor:"pointer", color:T.text, display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={()=>setCycleTab("overview")} style={{ width:"100%", padding:14, background:phase.color, border:"none", borderRadius:14, fontFamily:T.fb, fontWeight:600, fontSize:15, color:"#fff", cursor:"pointer", marginBottom:20 }}>
                  Save Log
                </button>
              </>)}
            </div>
          </div>
        )}

        {/* ════ MEDITATE ════ */}
        {tab==="meditate"&&(
          <div className="fi">
            <div className="section" style={{ paddingTop:8 }}>
              <div style={{ marginBottom:4 }}><div className="sh-t" style={{ fontFamily:T.fd, fontSize:28, fontWeight:900 }}>Guided Meditations</div></div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginBottom:18 }}>15 minutes or less — curated from YouTube</div>
              <div className="sub-tabs">
                {[{id:"all",label:"All"},{id:"cycle",label:`${phase.name} Phase`},{id:"calming",label:"Calming"},{id:"energizing",label:"Energizing"},{id:"focus",label:"Focus"},{id:"stressed",label:"Stress Relief"},{id:"soothing",label:"Soothing"}].map(f=>(
                  <PillBtn key={f.id} active={meditationFilter===f.id} onClick={()=>setMeditationFilter(f.id)}>{f.label}</PillBtn>
                ))}
              </div>

              {isFemale && (
                <div style={{ background:phase.light, border:`1px solid ${phase.color}30`, borderRadius:16, padding:16, marginBottom:18 }}>
                  <div style={{ fontFamily:T.fb, fontSize:11, fontWeight:600, letterSpacing:1, color:phase.color, textTransform:"uppercase", marginBottom:6 }}>Recommended for your {phase.name} phase</div>
                  <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, lineHeight:1.65 }}>
                    {phase.meditationMood==="calming"?"Gentle, restorative practices help honor your body's need for rest during menstruation.":phase.meditationMood==="energizing"?"Energizing meditations amplify the natural vitality of rising estrogen in your follicular phase.":phase.meditationMood==="focus"?"Focus-based practices align with your peak mental clarity during ovulation.":"Soothing practices help ease PMS symptoms and emotional sensitivity in your luteal phase."}
                  </div>
                </div>
              )}

              {(()=>{
                const list = meditationFilter==="all" ? Object.values(MEDITATIONS).flat() : meditationFilter==="cycle" ? (MEDITATIONS[phase.meditationMood]||MEDITATIONS.calming) : (MEDITATIONS[meditationFilter]||[]);
                return list.map((med,i)=>(
                  <div key={i} className="med-card" onClick={()=>setSelectedMeditation(med)}>
                    <div style={{ position:"relative" }}>
                      <div style={{ width:"100%", height:130, background:`linear-gradient(135deg,${T.bgMuted},${T.border})`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                        <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:700, color:T.text, letterSpacing:-1 }}>{med.title.split(" ").slice(0,3).join(" ")}</div>
                        <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, marginTop:4 }}>{med.channel}</div>
                      </div>
                      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(255,255,255,.9)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 16px rgba(26,23,20,.15)" }}>
                          <div style={{ width:0, height:0, borderTop:"9px solid transparent", borderBottom:"9px solid transparent", borderLeft:`15px solid ${T.accent}`, marginLeft:3 }}/>
                        </div>
                      </div>
                      <div style={{ position:"absolute", bottom:10, right:12, background:"rgba(26,23,20,.6)", borderRadius:6, padding:"3px 8px", fontFamily:T.fb, fontSize:11, color:"#fff", fontWeight:500 }}>{med.duration}</div>
                    </div>
                    <div style={{ padding:"14px 16px" }}>
                      <div style={{ fontFamily:T.fd, fontSize:16, fontWeight:700, color:T.text, marginBottom:4 }}>{med.title}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted }}>{med.channel}</span>
                        {med.tags.slice(0,2).map(t=><Badge key={t} color={T.teal}>{t}</Badge>)}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* ════ INSIGHTS ════ */}
        {tab==="insights"&&(
          <div className="fi">
            <div className="section" style={{ paddingTop:8, marginBottom:24 }}>
              <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1, marginBottom:18 }}>Weekly Trends</div>
              {primaryMetrics.map(key=>{
                const m=INSIGHTS[key]; const hist=weekHistory(key);
                const avg=Math.round(hist.reduce((a,b)=>a+b,0)/7); const best=Math.max(...hist);
                const todayVal=todayData[key]; const rng=todayVal?getRange(key,todayVal):null;
                return (
                  <div key={key} className="trend-card" onClick={()=>setActiveMetric(key)}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:rng?12:0 }}>
                      <div>
                        <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, letterSpacing:2, color:T.textMuted, textTransform:"uppercase", marginBottom:8 }}>{m.label}</div>
                        <div style={{ display:"flex", gap:20 }}>
                          <div><div style={{ fontFamily:T.fd, fontSize:22, fontWeight:700, color:T.text }}>{avg}</div><div style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted }}>avg {m.unit}</div></div>
                          <div><div style={{ fontFamily:T.fd, fontSize:22, fontWeight:700, color:m.color }}>{best}</div><div style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted }}>best</div></div>
                        </div>
                      </div>
                      <Spark data={hist} color={m.color} h={44} w={90}/>
                    </div>
                    {rng && <div style={{ padding:"10px 12px", background:rng.color+"12", borderRadius:10, borderLeft:`2px solid ${rng.color}` }}>
                      <div style={{ fontFamily:T.fb, fontSize:12, color:rng.color, lineHeight:1.65, fontWeight:500 }}>{rng.tip}</div>
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ════ REFLECT ════ */}
        {tab==="reflect"&&(
          <div className="fi">
            <div className="section" style={{ paddingTop:8 }}>
              <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1, marginBottom:4 }}>Daily Reflection</div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginBottom:18 }}>{new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                {[{label:"Energy Level",key:"energy",color:T.accent},{label:"Stress Level",key:"stress",color:T.gold}].map(s=>(
                  <div key={s.key} style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:18, padding:16 }}>
                    <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:10 }}>{s.label}</div>
                    <div style={{ display:"flex", gap:6 }}>
                      {[1,2,3,4,5].map(n=><button key={n} className={`scale-b ${reflection[s.key]===n?"on":""}`} style={{ ...(reflection[s.key]===n?{borderColor:s.color,background:s.color+"18",color:s.color}:{}) }} onClick={()=>setReflection(r=>({...r,[s.key]:n}))}>{n}</button>)}
                    </div>
                  </div>
                ))}
              </div>
              {[{key:"gratitude",label:"Gratitude — what went well today?",placeholder:"Today I'm grateful for…"},{key:"wins",label:"Today's wins",placeholder:"I made progress on…"},{key:"tomorrow",label:"Tomorrow's intention",placeholder:"Tomorrow I will focus on…"}].map(field=>(
                <div key={field.key} style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:18, padding:20, marginBottom:10 }}>
                  <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:10 }}>{field.label}</div>
                  <textarea className="r-ta" rows={field.key==="tomorrow"?2:3} placeholder={field.placeholder} value={reflection[field.key]} onChange={e=>setReflection(r=>({...r,[field.key]:e.target.value}))}/>
                </div>
              ))}
              <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:18, padding:20, marginBottom:10 }}>
                <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, letterSpacing:1.5, color:T.textMuted, textTransform:"uppercase", marginBottom:12 }}>Activities — tap all that apply</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {reflection.activities.map((act,i)=>(
                    <div key={i} className={`act-chip ${act.logged?"on":""}`} onClick={()=>setReflection(r=>({...r,activities:r.activities.map((a,j)=>j===i?{...a,logged:!a.logged}:a)}))}>
                      {act.label}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background:T.accentLight, border:`1px solid ${T.accentMid}40`, borderRadius:18, padding:20, marginBottom:24 }}>
                <div style={{ fontFamily:T.fb, fontSize:11, fontWeight:700, letterSpacing:1.5, color:T.accent, textTransform:"uppercase", marginBottom:10 }}>Today's Personalized Insight</div>
                <div style={{ fontFamily:T.fb, fontSize:14, color:T.textSub, lineHeight:1.75 }}>
                  {getBehaviorInsight()}
                  {isFemale && cycleDay ? ` You're in your ${phase.name.toLowerCase()} phase (day ${cycleDay}) — ${phase.meditationMood==="calming"?"honor the need for rest today.":phase.meditationMood==="energizing"?"channel this rising energy into something meaningful.":phase.meditationMood==="focus"?"use this peak clarity window for your most important work.":"gentle movement and extra self-care will serve you best right now."}` : ""}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════ SUPPORT / CHAT ════ */}
        {tab==="support"&&(
          <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 90px)", padding:"0 0 0" }} className="fi">
            <div style={{ padding:"20px 20px 16px", borderBottom:`1px solid ${T.border}`, background:T.bgCard }}>
              <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:900, color:T.text, letterSpacing:-1 }}>GRIND Support</div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginTop:2 }}>Ask anything about your health, metrics, or the app</div>
            </div>
            <div style={{ flex:1, overflowY:"auto", padding:"20px 20px 12px", display:"flex", flexDirection:"column", gap:12 }}>
              {chatMessages.map((msg,i)=>(
                <div key={i} className={msg.role==="user"?"chat-bubble-user":"chat-bubble-ai"} style={{ fontFamily:T.fb }}>
                  {msg.role==="assistant" && <div style={{ fontFamily:T.fb, fontSize:11, fontWeight:600, letterSpacing:1, color:T.textMuted, marginBottom:6, textTransform:"uppercase" }}>GRIND Assistant</div>}
                  {msg.text.split("**").map((part,j)=>j%2===0?<span key={j}>{part}</span>:<strong key={j}>{part}</strong>)}
                </div>
              ))}
              <div ref={chatEndRef}/>
            </div>
            {/* Quick replies */}
            <div style={{ padding:"0 16px 10px", display:"flex", gap:8, overflowX:"auto", scrollbarWidth:"none" }}>
              {["What is HRV?","How much water daily?","How does cycle tracking work?","Contact support"].map(q=>(
                <button key={q} onClick={()=>{setChatInput(q);setTimeout(()=>{ const ans=askAI(q); setChatMessages(prev=>[...prev,{role:"user",text:q},{role:"assistant",text:ans}]); setChatInput(""); },100);}}
                  style={{ padding:"8px 14px", borderRadius:20, border:`1px solid ${T.border}`, background:T.bgCard, fontFamily:T.fb, fontSize:12, color:T.textSub, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0, transition:"all .15s" }}>
                  {q}
                </button>
              ))}
            </div>
            <div style={{ padding:"10px 16px 24px", background:T.bgCard, borderTop:`1px solid ${T.border}`, display:"flex", gap:10 }}>
              <input value={chatInput} onChange={e=>setChatInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendChat()}
                placeholder="Ask me anything…"
                style={{ flex:1, padding:"14px 16px", background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:24, fontSize:15, color:T.text, outline:"none", transition:"border-color .2s", fontFamily:T.fb }}
                onFocus={e=>e.target.style.borderColor=T.accent} onBlur={e=>e.target.style.borderColor=T.border}/>
              <button onClick={sendChat} style={{ width:48, height:48, borderRadius:"50%", background:T.accent, border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <div style={{ width:0, height:0, borderTop:"7px solid transparent", borderBottom:"7px solid transparent", borderLeft:"12px solid #fff", marginLeft:3 }}/>
              </button>
            </div>
          </div>
        )}

        {/* ════ DEVICES ════ */}
        {tab==="devices"&&(
          <div className="fi">
            <div className="section" style={{ paddingTop:8 }}>
              <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1, marginBottom:4 }}>Connected Devices</div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginBottom:20 }}>Sync your wearables to auto-populate metrics</div>

              {/* ── WHOOP CARD ── */}
              {(()=>{
                const isConn = connectedDevices.includes("whoop") && whoopToken;
                return (
                  <div style={{ background:T.bgCard, border:`1.5px solid ${isConn?"#90CEC3":T.border}`, borderRadius:20, padding:20, marginBottom:12, transition:"all .2s" }}>
                    {/* Header row */}
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:isConn||showWhoopSetup?16:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:48, height:48, borderRadius:14, background:isConn?T.tealLight:"#1A1714", display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${isConn?"#90CEC3":"#333"}` }}>
                          <span style={{ fontFamily:T.fd, fontSize:11, fontWeight:900, color:isConn?T.teal:"#fff", letterSpacing:1 }}>WHP</span>
                        </div>
                        <div>
                          <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:T.text }}>WHOOP</div>
                          {isConn && whoopProfile && <div style={{ fontFamily:T.fb, fontSize:12, color:T.teal, marginTop:2 }}>{whoopProfile.first_name} {whoopProfile.last_name}</div>}
                          {!isConn && <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, marginTop:2 }}>HRV · Recovery · Strain · Sleep · RHR</div>}
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        {isConn && <Badge color={T.teal} bg={T.tealLight}>Live</Badge>}
                        {!isConn && !showWhoopSetup && (
                          <button onClick={()=>setShowWhoopSetup(true)} style={{ padding:"8px 16px", borderRadius:20, border:`1.5px solid ${T.accent}`, background:T.accentLight, color:T.accent, fontFamily:T.fb, fontSize:13, fontWeight:700, cursor:"pointer" }}>Connect</button>
                        )}
                        {isConn && (
                          <button onClick={()=>fetchWhoopData()} disabled={whoopLoading} style={{ padding:"8px 14px", borderRadius:20, border:`1.5px solid ${T.teal}`, background:T.tealLight, color:T.teal, fontFamily:T.fb, fontSize:12, fontWeight:700, cursor:"pointer", opacity:whoopLoading?.6:1 }}>
                            {whoopLoading ? "Syncing…" : "↻ Sync"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Setup flow — enter Client ID + Secret */}
                    {!isConn && showWhoopSetup && (
                      <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:16 }}>
                        <div style={{ background:T.blueLight, border:`1px solid ${T.blue}22`, borderRadius:12, padding:14, marginBottom:16 }}>
                          <div style={{ fontFamily:T.fb, fontSize:12, fontWeight:700, color:T.blue, marginBottom:6, letterSpacing:.5, textTransform:"uppercase" }}>One-time setup</div>
                          <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, lineHeight:1.7 }}>
                            1. Go to <span style={{ color:T.blue, fontWeight:700 }}>developer.whoop.com</span> and create an app<br/>
                            2. Set Redirect URI to: <span style={{ fontFamily:"monospace", fontSize:11, background:T.bgMuted, padding:"2px 6px", borderRadius:4, color:T.text }}>{window.location.origin+window.location.pathname}</span><br/>
                            3. Paste your Client ID and Secret below
                          </div>
                        </div>
                        <div className="form-grp">
                          <label className="form-lbl">Client ID</label>
                          <input className="txt-in" placeholder="Your WHOOP Client ID" value={whoopClientId}
                            onChange={e=>{ setWhoopClientId(e.target.value); localStorage.setItem("grind_whoop_client_id",e.target.value); }} />
                        </div>
                        <div className="form-grp">
                          <label className="form-lbl">Client Secret</label>
                          <input className="txt-in" type="password" placeholder="Your WHOOP Client Secret" value={whoopClientSecret}
                            onChange={e=>{ setWhoopClientSecret(e.target.value); localStorage.setItem("grind_whoop_client_secret",e.target.value); }} />
                        </div>
                        {whoopError && <div style={{ background:"#FEE8E8", border:"1px solid #F8A0A0", borderRadius:10, padding:"10px 14px", fontFamily:T.fb, fontSize:13, color:"#C04040", marginBottom:12 }}>{whoopError}</div>}
                        <div style={{ display:"flex", gap:10 }}>
                          <button onClick={()=>{ if(whoopClientId){ window.location.href=whoopAuthUrl(whoopClientId); } else { setWhoopError("Enter your Client ID first."); }}}
                            style={{ flex:1, padding:"13px", borderRadius:14, border:"none", background:T.text, color:"#fff", fontFamily:T.fb, fontSize:15, fontWeight:700, cursor:"pointer" }}>
                            Authorize with WHOOP →
                          </button>
                          <button onClick={()=>{setShowWhoopSetup(false);setWhoopError(null);}}
                            style={{ padding:"13px 18px", borderRadius:14, border:`1.5px solid ${T.border}`, background:"transparent", color:T.textSub, fontFamily:T.fb, fontSize:14, cursor:"pointer" }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Connected state — show live data */}
                    {isConn && (
                      <div>
                        {whoopLoading && <div style={{ fontFamily:T.fb, fontSize:13, color:T.teal, marginBottom:12 }}>⟳ Fetching latest data from WHOOP…</div>}
                        {whoopError && <div style={{ background:"#FEE8E8", border:"1px solid #F8A0A0", borderRadius:10, padding:"10px 14px", fontFamily:T.fb, fontSize:13, color:"#C04040", marginBottom:12 }}>{whoopError}</div>}
                        {whoopData?.metrics && Object.keys(whoopData.metrics).length > 0 && (
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
                            {Object.entries(whoopData.metrics).map(([k,v])=>{
                              const meta = INSIGHTS[k];
                              if(!meta||!v) return null;
                              return (
                                <div key={k} style={{ background:T.bgMuted, borderRadius:12, padding:"10px 12px", textAlign:"center" }}>
                                  <div style={{ fontFamily:T.fd, fontSize:20, fontWeight:700, color:meta.color||T.text }}>{v}</div>
                                  <div style={{ fontFamily:T.fb, fontSize:10, color:T.textMuted, fontWeight:600, letterSpacing:.5, marginTop:2 }}>{meta.unit}</div>
                                  <div style={{ fontFamily:T.fb, fontSize:11, color:T.textSub, marginTop:2 }}>{meta.label}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          {whoopLastSync && <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted }}>Last synced {whoopLastSync}</div>}
                          <button onClick={disconnectWhoop} style={{ fontFamily:T.fb, fontSize:12, color:T.rose, background:"transparent", border:"none", cursor:"pointer", padding:"4px 0" }}>Disconnect</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── OTHER WEARABLES ── */}
              {WEARABLES.filter(w=>w.id!=="whoop").map(w=>{
                const isConn=connectedDevices.includes(w.id);
                return (
                  <div key={w.id} className={`dev-conn ${isConn?"on":""}`} style={{ opacity:.75 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:44, height:44, borderRadius:12, background:isConn?T.tealLight:T.bgMuted, border:`1px solid ${isConn?"#90CEC3":T.border}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <div style={{ fontFamily:T.fd, fontSize:10, fontWeight:700, color:isConn?T.teal:T.textMuted }}>{w.name.slice(0,3).toUpperCase()}</div>
                        </div>
                        <div>
                          <div style={{ fontFamily:T.fd, fontSize:17, fontWeight:700, color:T.text }}>{w.name}</div>
                          <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, marginTop:2 }}>Native app integration — coming soon</div>
                        </div>
                      </div>
                      <Badge color={T.textMuted} bg={T.bgMuted}>Soon</Badge>
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10 }}>
                      {w.metrics.map(m=><div key={m} style={{ padding:"3px 10px", borderRadius:8, background:T.bgCard, fontFamily:T.fb, fontSize:10, color:T.textMuted, border:`1px solid ${T.border}`, fontWeight:500 }}>{INSIGHTS[m]?.label||m}</div>)}
                    </div>
                  </div>
                );
              })}

              <div style={{ background:T.bgMuted, borderRadius:18, padding:20, marginTop:6 }}>
                <div style={{ fontFamily:T.fd, fontSize:16, fontWeight:700, color:T.text, marginBottom:8 }}>How Sync Works</div>
                <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, lineHeight:1.8 }}>WHOOP connects via OAuth2 — your data stays private. Apple Watch, Garmin, and Oura sync will require the native GRIND iOS app (coming soon). For now, use the Today tab to log metrics manually alongside WHOOP sync.</div>
              </div>
            </div>
          </div>
        )}


        {/* ════ PROFILE ════ */}
        {tab==="profile"&&(
          <div className="fi">
            <div className="section" style={{ paddingTop:8 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
                <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1 }}>My Profile</div>
                <button onClick={()=>setProfileForm({...profile})} style={{ padding:"8px 16px", background:T.accent, border:"none", borderRadius:20, fontFamily:T.fb, fontSize:13, fontWeight:600, color:"#fff", cursor:"pointer" }}>Edit</button>
              </div>

              {/* Avatar */}
              <div style={{ display:"flex", alignItems:"center", gap:18, marginBottom:24, background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:20, padding:20 }}>
                <div style={{ width:72, height:72, borderRadius:"50%", background:`linear-gradient(135deg,${T.accent},${T.teal})`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <div style={{ fontFamily:T.fd, fontSize:30, fontWeight:900, color:"#fff" }}>
                    {(profile.name||user?.name||"G").charAt(0).toUpperCase()}
                  </div>
                </div>
                <div>
                  <div style={{ fontFamily:T.fd, fontSize:22, fontWeight:700, color:T.text, letterSpacing:-.5 }}>{profile.name||user?.name||"—"}</div>
                  {profile.username&&<div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginTop:2 }}>@{profile.username}</div>}
                  {profile.city&&<div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginTop:2 }}>{profile.city}{profile.state?", "+profile.state:""}</div>}
                </div>
              </div>

              {/* Stats row */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:20 }}>
                {[
                  {label:"Day Streak", val:streak||0, color:T.accent},
                  {label:"Days Logged", val:Object.keys(logData).length, color:T.teal},
                  {label:"Activities", val:activityLog.length, color:T.blue},
                ].map(s=>(
                  <div key={s.label} style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:14, padding:"14px 10px", textAlign:"center" }}>
                    <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:900, color:s.color }}>{s.val}</div>
                    <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, color:T.textMuted, letterSpacing:.5, marginTop:2 }}>{s.label.toUpperCase()}</div>
                  </div>
                ))}
              </div>

              {/* Profile details */}
              <div className="info-box">
                <div className="info-lbl">Personal Info</div>
                {[
                  {label:"Email", val:profile.email||user?.email||"—"},
                  {label:"Age", val:profile.age||"—"},
                  {label:"Location", val:(profile.city&&profile.state)?`${profile.city}, ${profile.state}`:profile.city||profile.state||"—"},
                  {label:"Favorite Activity", val:profile.favActivity||"—"},
                ].map((row,i)=>(
                  <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:i<3?`1px solid ${T.border}`:"none" }}>
                    <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, fontWeight:500 }}>{row.label}</div>
                    <div style={{ fontFamily:T.fb, fontSize:13, color:T.text, fontWeight:500, textAlign:"right", maxWidth:"60%" }}>{row.val}</div>
                  </div>
                ))}
              </div>

              {profile.bio&&(
                <div className="info-box">
                  <div className="info-lbl">About Me</div>
                  <div style={{ fontFamily:T.fb, fontSize:14, color:T.textSub, lineHeight:1.7 }}>{profile.bio}</div>
                </div>
              )}

              {/* Recent Activity */}
              <div style={{ fontFamily:T.fd, fontSize:20, fontWeight:700, color:T.text, marginBottom:14, marginTop:4 }}>Activity Log</div>
              <button className="add-btn" onClick={()=>{setActivityForm({sport:"Running",duration:30,intensity:"Moderate",notes:"",date:today()});setShowActivityModal(true);}}>
                + Log an Activity
              </button>
              {activityLog.length===0&&(
                <div style={{ background:T.bgMuted, borderRadius:14, padding:20, textAlign:"center" }}>
                  <div style={{ fontFamily:T.fb, fontSize:14, color:T.textMuted, lineHeight:1.7 }}>No activities logged yet. Tap above to record your first workout or sport session.</div>
                </div>
              )}
              {[...activityLog].reverse().slice(0,10).map((a,i)=>(
                <div key={i} style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:16, padding:16, marginBottom:10, display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:46, height:46, borderRadius:12, background:T.accentLight, border:`1px solid ${T.accent}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <div style={{ fontFamily:T.fd, fontSize:10, fontWeight:700, color:T.accent, textAlign:"center" }}>{a.sport.slice(0,3).toUpperCase()}</div>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:T.fd, fontSize:15, fontWeight:700, color:T.text }}>{a.sport}</div>
                    <div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, marginTop:2 }}>{a.duration} min · {a.intensity}{a.notes?" · "+a.notes:""}</div>
                  </div>
                  <div style={{ fontFamily:T.fb, fontSize:11, color:T.textMuted }}>{a.date===today()?"Today":a.date}</div>
                </div>
              ))}

              {/* Sign out */}
              <button onClick={()=>{ localStorage.removeItem("grind_user"); setUser(null); setScreen("welcome"); }}
                style={{ width:"100%", padding:14, background:"transparent", border:`1.5px solid ${T.border}`, borderRadius:14, fontFamily:T.fb, fontWeight:600, fontSize:14, color:T.textMuted, cursor:"pointer", marginTop:16, marginBottom:24 }}>
                Sign Out
              </button>
            </div>
          </div>
        )}

        {/* ── TAB BAR ── */}
        <div className="tab-bar">
          {tabs.map(t=>(
            <button key={t.id} className={`tb ${tab===t.id?"active":""}`} onClick={()=>setTab(t.id)}>
              <div className="tb-dot"/>
              <div style={{ fontFamily:T.fd, fontSize:20, fontWeight:tab===t.id?700:400, color:tab===t.id?T.accent:T.textMuted, lineHeight:1 }}>
                {t.id==="today"?"◎":t.id==="cycle"?"◑":t.id==="meditate"?"◯":t.id==="insights"?"△":t.id==="reflect"?"✦":t.id==="support"?"◇":t.id==="devices"?"⊞":t.id==="profile"?"◉":"·"}
              </div>
              <span className="tb-l">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── METRIC DETAIL MODAL ── */}
      {activeMetric&&(()=>{
        const m=INSIGHTS[activeMetric]; const val=todayData[activeMetric]; const rng=val?getRange(activeMetric,val):null;
        const hist=weekHistory(activeMetric);
        return (
          <Modal onClose={()=>setActiveMetric(null)}>
            <div style={{ fontFamily:T.fd, fontSize:28, fontWeight:900, color:T.text, letterSpacing:-1, textAlign:"center", marginBottom:4 }}>{m.label}</div>
            <div style={{ fontFamily:T.fb, fontSize:14, color:T.textMuted, textAlign:"center", lineHeight:1.7, marginBottom:20 }}>{m.description}</div>
            {rng && <div style={{ background:rng.color+"12", borderLeft:`3px solid ${rng.color}`, borderRadius:12, padding:16, marginBottom:16 }}>
              <div style={{ fontFamily:T.fd, fontSize:18, fontWeight:700, color:rng.color, marginBottom:6 }}>{rng.label} — {val} {m.unit}</div>
              <div style={{ fontFamily:T.fb, fontSize:14, color:T.textSub, lineHeight:1.65 }}>{rng.tip}</div>
            </div>}
            {m.ranges.length>1 && <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:700, letterSpacing:2, color:T.textMuted, marginBottom:10, textTransform:"uppercase" }}>All Ranges</div>
              {m.ranges.map(r=><div key={r.label} style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:8 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:r.color, marginTop:5, flexShrink:0 }}/>
                <div><div style={{ fontFamily:T.fb, fontSize:13, fontWeight:600, color:r.color }}>{r.label}</div><div style={{ fontFamily:T.fb, fontSize:12, color:T.textMuted, lineHeight:1.5 }}>{r.tip}</div></div>
              </div>)}
            </div>}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:700, letterSpacing:2, color:T.textMuted, marginBottom:8, textTransform:"uppercase" }}>7-Day Trend</div>
              <Spark data={hist} color={m.color} h={50} w={340}/>
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:20 }}>
              {m.device.map(d=><div key={d} style={{ fontFamily:T.fb, fontSize:11, background:T.bgMuted, borderRadius:8, padding:"4px 10px", color:T.textSub, border:`1px solid ${T.border}`, fontWeight:500 }}>{d}</div>)}
            </div>
            <button className="save-btn" style={{ background:m.color }} onClick={()=>{setEditMetric(activeMetric);setInputVal(val?String(val):"");setActiveMetric(null);}}>Update Value</button>
            <div className="cancel-lnk" onClick={()=>setActiveMetric(null)}>Close</div>
          </Modal>
        );
      })()}

      {/* ── LOG MODAL ── */}
      {editMetric&&(()=>{
        const m=INSIGHTS[editMetric];
        const stepMap={steps:500,calories:50,water:8,weight:0.5,sleep:0.5,hrv:1,rhr:1,spo2:1,strain:0.5,recovery:1,reading:5,mindfulness:5};
        const step=stepMap[editMetric]||1;
        return (
          <Modal onClose={()=>setEditMetric(null)}>
            <div style={{ fontFamily:T.fd, fontSize:26, fontWeight:900, color:T.text, textAlign:"center", marginBottom:4 }}>Log {m.label}</div>
            <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, textAlign:"center", marginBottom:20 }}>{m.unit}</div>
            {editMetric==="mood"?(
              <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                {["Very Low","Low","Neutral","Good","Excellent"].map((label,i)=>(
                  <button key={i} onClick={()=>setInputVal(String(i+1))}
                    style={{ flex:1, padding:"14px 4px", background:inputVal===String(i+1)?T.accent+"18":T.bg, border:`1.5px solid ${inputVal===String(i+1)?T.accent:T.border}`, borderRadius:12, cursor:"pointer", transition:"all .15s" }}>
                    <div style={{ fontFamily:T.fb, fontSize:10, fontWeight:600, color:inputVal===String(i+1)?T.accent:T.textMuted, textAlign:"center", letterSpacing:.3 }}>{label}</div>
                  </button>
                ))}
              </div>
            ):(
              <div style={{ display:"flex", gap:10, alignItems:"center", justifyContent:"center", margin:"10px 0" }}>
                <button className="spin-b" onClick={()=>setInputVal(v=>String(Math.max(0,(parseFloat(v)||0)-step)))}>−</button>
                <input className="num-in" type="number" value={inputVal} onChange={e=>setInputVal(e.target.value)} placeholder="0" autoFocus style={{ flex:1, maxWidth:160 }}/>
                <button className="spin-b" onClick={()=>setInputVal(v=>String((parseFloat(v)||0)+step))}>+</button>
              </div>
            )}
            <button className="save-btn" style={{ background:m.color }} onClick={()=>saveMetric(editMetric,inputVal||(editMetric==="mood"?"3":"0"))}>Save</button>
            <div className="cancel-lnk" onClick={()=>setEditMetric(null)}>Cancel</div>
          </Modal>
        );
      })()}

      {/* ── MEDITATION MODAL ── */}
      {selectedMeditation&&(
        <Modal onClose={()=>setSelectedMeditation(null)}>
          <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:900, color:T.text, textAlign:"center", lineHeight:1.3, marginBottom:6 }}>{selectedMeditation.title}</div>
          <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, textAlign:"center", marginBottom:20 }}>{selectedMeditation.channel}  ·  {selectedMeditation.duration}</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, justifyContent:"center", marginBottom:20 }}>
            {selectedMeditation.tags.map(t=><Badge key={t} color={T.teal}>{t}</Badge>)}
          </div>
          <div style={{ background:T.bgMuted, borderRadius:16, padding:24, marginBottom:20, textAlign:"center" }}>
            <div style={{ fontFamily:T.fd, fontSize:14, color:T.textSub, lineHeight:1.7, marginBottom:16 }}>
              This meditation opens on YouTube in your browser. Find a comfortable position, close your eyes, and let yourself fully arrive.
            </div>
            <div style={{ width:64, height:64, borderRadius:"50%", background:T.accent, margin:"0 auto 12px", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:0, height:0, borderTop:"11px solid transparent", borderBottom:"11px solid transparent", borderLeft:"18px solid #fff", marginLeft:4 }}/>
            </div>
            <div style={{ fontFamily:T.fb, fontSize:13, fontWeight:600, color:T.accent }}>Ready when you are</div>
          </div>
          {isFemale && <div style={{ background:phase.light, border:`1px solid ${phase.color}30`, borderRadius:14, padding:16, marginBottom:20 }}>
            <div style={{ fontFamily:T.fb, fontSize:11, fontWeight:700, letterSpacing:1, color:phase.color, textTransform:"uppercase", marginBottom:6 }}>Why this for your {phase.name} phase</div>
            <div style={{ fontFamily:T.fb, fontSize:13, color:T.textSub, lineHeight:1.65 }}>
              {phase.meditationMood==="calming"?"Gentle practices support your body during menstruation by lowering cortisol and easing cramping.":phase.meditationMood==="energizing"?"Energizing breathwork amplifies the natural vitality surge from rising estrogen in your follicular phase.":phase.meditationMood==="focus"?"Peak estrogen and testosterone create optimal conditions for focus meditation and intention setting.":"Soothing practices help counter progesterone-related mood shifts during your luteal phase."}
            </div>
          </div>}
          <a href={`https://www.youtube.com/watch?v=${selectedMeditation.videoId}`} target="_blank" rel="noopener noreferrer"
            style={{ display:"block", width:"100%", padding:15, background:T.accent, border:"none", borderRadius:14, fontFamily:T.fb, fontWeight:700, fontSize:16, color:"#fff", cursor:"pointer", textAlign:"center", textDecoration:"none" }}>
            Open on YouTube
          </a>
          <div className="cancel-lnk" onClick={()=>setSelectedMeditation(null)}>Close</div>
        </Modal>
      )}


      {/* ── ACTIVITY LOG MODAL ── */}
      {showActivityModal&&(
        <Modal onClose={()=>setShowActivityModal(false)}>
          <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:900, color:T.text, marginBottom:6 }}>Log Activity</div>
          <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, marginBottom:22 }}>Record a workout, sport, or movement session</div>

          <div className="form-grp">
            <label className="form-lbl">Sport or Activity</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, maxHeight:180, overflowY:"auto" }}>
              {SPORTS.map(s=>(
                <div key={s} className={`symptom-chip ${activityForm.sport===s?"on":""}`} onClick={()=>setActivityForm(f=>({...f,sport:s}))}>{s}</div>
              ))}
            </div>
          </div>

          <div className="form-grp">
            <label className="form-lbl">Duration (minutes)</label>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <button className="spin-b" onClick={()=>setActivityForm(f=>({...f,duration:Math.max(5,f.duration-5)}))}>−</button>
              <div style={{ flex:1, textAlign:"center", fontFamily:T.fd, fontSize:32, fontWeight:700, color:T.text }}>{activityForm.duration}</div>
              <button className="spin-b" onClick={()=>setActivityForm(f=>({...f,duration:f.duration+5}))}>+</button>
            </div>
          </div>

          <div className="form-grp">
            <label className="form-lbl">Intensity</label>
            <div style={{ display:"flex", gap:8 }}>
              {["Easy","Moderate","Hard","All Out"].map(lvl=>(
                <button key={lvl} onClick={()=>setActivityForm(f=>({...f,intensity:lvl}))}
                  style={{ flex:1, padding:"10px 4px", borderRadius:10, border:`1.5px solid ${activityForm.intensity===lvl?T.accent:T.border}`, background:activityForm.intensity===lvl?T.accentLight:T.bg, fontFamily:T.fb, fontSize:11, fontWeight:600, color:activityForm.intensity===lvl?T.accent:T.textSub, cursor:"pointer" }}>{lvl}</button>
              ))}
            </div>
          </div>

          <div className="form-grp">
            <label className="form-lbl">Notes (optional)</label>
            <input className="txt-in" placeholder="How did it feel? Personal record? Location?" value={activityForm.notes} onChange={e=>setActivityForm(f=>({...f,notes:e.target.value}))}/>
          </div>

          <button className="save-btn" style={{ background:T.accent }} onClick={()=>{
            setActivityLog(prev=>[...prev,{...activityForm}]);
            setShowActivityModal(false);
            // Auto-log steps estimate based on activity
            const stepMap={Running:activityForm.duration*150,Walking:activityForm.duration*100,Cycling:activityForm.duration*80,Swimming:activityForm.duration*60};
            const estSteps = stepMap[activityForm.sport]||activityForm.duration*70;
            const existing = todayData.steps||0;
            saveMetric("steps", existing+estSteps);
          }}>Save Activity</button>
          <div className="cancel-lnk" onClick={()=>setShowActivityModal(false)}>Cancel</div>
        </Modal>
      )}

      {/* ── PROFILE EDIT MODAL ── */}
      {profileForm&&(
        <Modal onClose={()=>setProfileForm(null)}>
          <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:900, color:T.text, marginBottom:22 }}>Edit Profile</div>
          {[
            {key:"name",label:"Full Name",placeholder:"Your full name"},
            {key:"username",label:"Username",placeholder:"@handle"},
            {key:"email",label:"Email",placeholder:"you@email.com"},
            {key:"age",label:"Age",placeholder:"Your age"},
            {key:"city",label:"City",placeholder:"City"},
            {key:"state",label:"State",placeholder:"State / Province"},
            {key:"favActivity",label:"Favorite Sport or Activity",placeholder:"e.g. Basketball, Yoga, Running"},
          ].map(field=>(
            <div key={field.key} className="form-grp">
              <label className="form-lbl">{field.label}</label>
              <input className="txt-in" placeholder={field.placeholder} value={profileForm[field.key]||""} onChange={e=>setProfileForm(f=>({...f,[field.key]:e.target.value}))}/>
            </div>
          ))}
          <div className="form-grp">
            <label className="form-lbl">About Me</label>
            <textarea className="r-ta" rows={3} placeholder="A little about yourself…" value={profileForm.bio||""} onChange={e=>setProfileForm(f=>({...f,bio:e.target.value}))}/>
          </div>
          <button className="save-btn" style={{ background:T.accent }} onClick={()=>{
            const updated={...profileForm};
            setProfile(updated);
            if(user) setUser(prev=>({...prev,name:updated.name||prev.name}));
            setProfileForm(null);
          }}>Save Profile</button>
          <div className="cancel-lnk" onClick={()=>setProfileForm(null)}>Cancel</div>
        </Modal>
      )}

      {/* ── REMINDER MODAL ── */}
      {showReminderModal&&(
        <Modal onClose={()=>setShowReminderModal(false)}>
          <div style={{ fontFamily:T.fd, fontSize:24, fontWeight:900, color:T.text, marginBottom:22 }}>{editingReminder?"Edit Reminder":"New Reminder"}</div>
          <div className="form-grp"><label className="form-lbl">Label</label><input className="txt-in" placeholder="e.g. Morning Walk" value={reminderForm.label} onChange={e=>setReminderForm(f=>({...f,label:e.target.value}))} autoFocus/></div>
          <div className="form-grp"><label className="form-lbl">Time</label><input className="time-in" type="time" value={reminderForm.time} onChange={e=>setReminderForm(f=>({...f,time:e.target.value}))}/></div>
          <div className="form-grp">
            <label className="form-lbl">Repeat Days</label>
            <div style={{ display:"flex", gap:6 }}>{DAYS_W.map((d,i)=><button key={i} className={`day-tog ${reminderForm.days.includes(i)?"on":""}`} onClick={()=>setReminderForm(f=>({...f,days:f.days.includes(i)?f.days.filter(x=>x!==i):[...f.days,i]}))}>{d}</button>)}</div>
          </div>
          <div className="form-grp">
            <label className="form-lbl">Color</label>
            <div style={{ display:"flex", gap:8 }}>{[T.accent,T.teal,T.blue,T.gold,T.rose,"#9060B8"].map(c=><button key={c} onClick={()=>setReminderForm(f=>({...f,color:c}))} style={{ width:32,height:32,borderRadius:"50%",border:`3px solid ${reminderForm.color===c?"#1A1714":"transparent"}`,background:c,cursor:"pointer" }}/>)}</div>
          </div>
          <button className="save-btn" style={{ background:reminderForm.color }} onClick={()=>{if(!reminderForm.label.trim())return;if(editingReminder)setReminders(prev=>prev.map(r=>r.id===editingReminder?{...r,...reminderForm}:r));else setReminders(prev=>[...prev,{...reminderForm,id:"r"+Date.now(),enabled:true}]);setShowReminderModal(false);}}>
            {editingReminder?"Save Changes":"Create Reminder"}
          </button>
          {editingReminder&&(deleteConfirm===editingReminder?(
            <div>
              <div style={{ fontFamily:T.fb, fontSize:13, color:T.textMuted, textAlign:"center", margin:"14px 0 8px" }}>Are you sure you want to delete this reminder?</div>
              <div style={{ display:"flex", gap:10 }}>
                <button style={{ flex:1,padding:12,background:T.rose,border:"none",borderRadius:10,fontFamily:T.fb,fontSize:14,fontWeight:600,color:"#fff",cursor:"pointer" }} onClick={()=>{setReminders(prev=>prev.filter(r=>r.id!==editingReminder));setDeleteConfirm(null);setShowReminderModal(false);}}>Delete</button>
                <button style={{ flex:1,padding:12,background:T.bgMuted,border:"none",borderRadius:10,fontFamily:T.fb,fontSize:14,color:T.textSub,cursor:"pointer" }} onClick={()=>setDeleteConfirm(null)}>Keep It</button>
              </div>
            </div>
          ):(
            <button style={{ width:"100%",padding:12,background:"transparent",border:`1.5px solid ${T.rose}30`,borderRadius:12,fontFamily:T.fb,fontSize:14,color:T.rose,cursor:"pointer",marginTop:8 }} onClick={()=>setDeleteConfirm(editingReminder)}>Delete Reminder</button>
          ))}
          <div className="cancel-lnk" onClick={()=>setShowReminderModal(false)}>Cancel</div>
        </Modal>
      )}
    </div>
  );
}
